import React, { useState, useEffect, useCallback } from 'react';
import { 
  Lock, Users, Calendar, Mic2, Package, Palette, Image, Upload,
  Plus, Trash2, Save, X, Edit2, Eye, EyeOff,
  Loader2, AlertCircle, CheckCircle, RefreshCw, ExternalLink,
  Building2, Globe2, Settings, Type, Sliders, Download, Search,
  DollarSign, UserCheck, UserX, Clock, Ticket, Mail, Briefcase, LogOut,
  FileCheck, FileClock, XCircle, ArrowRight, ListTodo, FileText,
  ChevronUp, ChevronDown, Link2, FolderOpen, GripVertical,
  LayoutDashboard, Navigation, MapPin, Scale, Menu, Home, Layers
} from 'lucide-react';
import PageBuilder from '../components/PageBuilder';
import GlobalStylingTab from '../components/cms/GlobalStylingTab';
import TenantManagementTab from '../components/cms/TenantManagementTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CMSAdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [userRole, setUserRole] = useState('admin'); // 'admin' = full access, 'ops' = operations only
  const [activeTab, setActiveTab] = useState('registrations');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  // Session timeout in milliseconds (2 hours)
  const SESSION_TIMEOUT = 2 * 60 * 60 * 1000;

  // CMS Data states
  const [partners, setPartners] = useState([]);
  const [sponsorHeroes, setSponsorHeroes] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [ticketPasses, setTicketPasses] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [palettes, setPalettes] = useState([]);
  const [interactivePresets, setInteractivePresets] = useState([]);
  const [fonts, setFonts] = useState({ heading: [], body: [] });
  const [fontScales, setFontScales] = useState([]);
  const [iconLibraries, setIconLibraries] = useState([]);

  // Operations Data states
  const [registrations, setRegistrations] = useState([]);
  const [registrationStats, setRegistrationStats] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorStats, setSponsorStats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteStats, setInviteStats] = useState(null);

  // Pending changes (for approver)
  const [pendingChanges, setPendingChanges] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  
  // My submissions (for cmsadmin to see their pending/rejected items)
  const [mySubmissions, setMySubmissions] = useState([]);

  // Tasks and Documents
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [taskFilter, setTaskFilter] = useState('all'); // all, not_started, in_progress, complete
  const [taskPhaseFilter, setTaskPhaseFilter] = useState('all');
  const [documentFilter, setDocumentFilter] = useState('all');

  // Site Configuration (Page Builder Foundation)
  const [siteConfig, setSiteConfig] = useState(null);
  const [siteConfigTab, setSiteConfigTab] = useState('branding'); // branding, event, navigation, footer, legal

  // Page Builder state
  const [selectedPage, setSelectedPage] = useState('home');
  const [allPages, setAllPages] = useState([]);
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [newPageData, setNewPageData] = useState({ title: '', slug: '', description: '' });

  // Edit states
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({});
  
  // Theme customization states
  const [showCustomColors, setShowCustomColors] = useState(false);
  const [customColorMode, setCustomColorMode] = useState('light');

  // Search/filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTicketType, setFilterTicketType] = useState('all');

  // Helper to check if user has full CMS access
  const hasFullAccess = userRole === 'admin' || userRole === 'approver';
  
  // Helper to check if user is approver
  const isApprover = userRole === 'approver';

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const storedSession = localStorage.getItem('cms_session');
        if (storedSession) {
          const session = JSON.parse(storedSession);
          const now = Date.now();
          
          // Check if session is still valid (not expired)
          if (session.expiry > now) {
            // Verify credentials and get role with API call
            const authString = btoa(`${session.username}:${session.password}`);
            const response = await fetch(`${BACKEND_URL}/api/auth/role`, {
              headers: { 'Authorization': `Basic ${authString}` }
            });
            
            if (response.ok) {
              const data = await response.json();
              setCredentials({ username: session.username, password: session.password });
              setUserRole(data.role);
              setIsAuthenticated(true);
              // Refresh session expiry on successful validation
              updateSessionExpiry(session.username, session.password, data.role);
            } else {
              // Invalid credentials, clear session
              localStorage.removeItem('cms_session');
            }
          } else {
            // Session expired, clear it
            localStorage.removeItem('cms_session');
          }
        }
      } catch (err) {
        console.error('Session check failed:', err);
        localStorage.removeItem('cms_session');
      } finally {
        setIsCheckingSession(false);
      }
    };
    
    checkExistingSession();
  }, []);

  // Update session expiry (called on activity)
  const updateSessionExpiry = (username, password, role = userRole) => {
    const session = {
      username,
      password,
      role,
      expiry: Date.now() + SESSION_TIMEOUT
    };
    localStorage.setItem('cms_session', JSON.stringify(session));
  };

  // Refresh session on any authenticated action
  useEffect(() => {
    if (isAuthenticated && credentials.username) {
      updateSessionExpiry(credentials.username, credentials.password);
    }
  }, [isAuthenticated, activeTab, editingItem, showAddForm]);

  const getAuthHeader = useCallback(() => {
    const authString = btoa(`${credentials.username}:${credentials.password}`);
    return { 'Authorization': `Basic ${authString}` };
  }, [credentials]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const authString = btoa(`${credentials.username}:${credentials.password}`);
      
      // First, verify credentials and get role
      const roleResponse = await fetch(`${BACKEND_URL}/api/auth/role`, {
        headers: { 'Authorization': `Basic ${authString}` }
      });

      if (!roleResponse.ok) {
        if (roleResponse.status === 401) throw new Error('Invalid credentials');
        throw new Error('Authentication failed');
      }

      const roleData = await roleResponse.json();
      setUserRole(roleData.role);
      
      // Store session in localStorage with role
      updateSessionExpiry(credentials.username, credentials.password, roleData.role);
      setIsAuthenticated(true);
      loadAllData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cms_session');
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    setUserRole('admin');
  };

  const loadAllData = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const headers = getAuthHeader();
      
      // Operations data (accessible to all admins)
      const [regsRes, regStatsRes, sponsorsRes, sponsorStatsRes, invitesRes, inviteStatsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/registrations`, { headers }),
        fetch(`${BACKEND_URL}/api/registrations/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/sponsors`, { headers }),
        fetch(`${BACKEND_URL}/api/sponsors/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/invites`, { headers }),
        fetch(`${BACKEND_URL}/api/invites/stats`, { headers })
      ]);
      
      if (regsRes.ok) setRegistrations(await regsRes.json());
      if (regStatsRes.ok) setRegistrationStats(await regStatsRes.json());
      if (sponsorsRes.ok) setSponsors(await sponsorsRes.json());
      if (sponsorStatsRes.ok) setSponsorStats(await sponsorStatsRes.json());
      if (invitesRes.ok) setInvites(await invitesRes.json());
      if (inviteStatsRes.ok) setInviteStats(await inviteStatsRes.json());

      // Tasks and Documents (accessible to all admins)
      const [tasksRes, docsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/tasks`, { headers }),
        fetch(`${BACKEND_URL}/api/documents`, { headers })
      ]);
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (docsRes.ok) setDocuments(await docsRes.json());

      // CMS content data (only for full admin)
      if (hasFullAccess) {
        const [partnersRes, heroesRes, agendaRes, speakersRes, packagesRes, ticketPassesRes, settingsRes, palettesRes, pendingRes, pendingCountRes, siteConfigRes, pagesRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/cms/partners/all`, { headers }),
          fetch(`${BACKEND_URL}/api/cms/sponsor-heroes/all`, { headers }),
          fetch(`${BACKEND_URL}/api/cms/agenda/all`, { headers }),
          fetch(`${BACKEND_URL}/api/cms/speakers/all`, { headers }),
          fetch(`${BACKEND_URL}/api/cms/packages/all`, { headers }),
          fetch(`${BACKEND_URL}/api/cms/ticket-passes/all`, { headers }),
          fetch(`${BACKEND_URL}/api/cms/settings`),
          fetch(`${BACKEND_URL}/api/cms/settings/palettes`),
          fetch(`${BACKEND_URL}/api/cms/pending`, { headers }),
          fetch(`${BACKEND_URL}/api/cms/pending/count`, { headers }),
          fetch(`${BACKEND_URL}/api/site-config`),
          fetch(`${BACKEND_URL}/api/pages`)
        ]);
        
        if (partnersRes.ok) setPartners(await partnersRes.json());
        if (heroesRes.ok) setSponsorHeroes(await heroesRes.json());
        if (agendaRes.ok) setAgenda(await agendaRes.json());
        if (speakersRes.ok) setSpeakers(await speakersRes.json());
        if (packagesRes.ok) setPackages(await packagesRes.json());
        if (ticketPassesRes.ok) setTicketPasses(await ticketPassesRes.json());
        if (settingsRes.ok) setSiteSettings(await settingsRes.json());
        if (siteConfigRes.ok) setSiteConfig(await siteConfigRes.json());
        if (pagesRes.ok) setAllPages(await pagesRes.json());
        if (palettesRes.ok) {
          const data = await palettesRes.json();
          setPalettes(data.palettes || []);
          setInteractivePresets(data.interactive_presets || []);
          setFonts(data.fonts || { heading: [], body: [] });
          setFontScales(data.font_scales || []);
          setIconLibraries(data.icon_libraries || []);
        }
        if (pendingRes.ok) setPendingChanges(await pendingRes.json());
        if (pendingCountRes.ok) {
          const countData = await pendingCountRes.json();
          setPendingCount(countData.count || 0);
        }
        
        // For cmsadmin (non-approver), fetch their own submissions
        if (!isApprover) {
          const allChangesRes = await fetch(`${BACKEND_URL}/api/cms/pending/all`, { headers });
          if (allChangesRes.ok) {
            const allChanges = await allChangesRes.json();
            // Filter to show only this user's submissions that are pending or recently rejected
            const userSubmissions = allChanges.filter(c => 
              c.submitted_by === credentials.username && 
              (c.status === 'pending' || c.status === 'rejected')
            );
            setMySubmissions(userSubmissions);
          }
        }
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader, hasFullAccess]);

  useEffect(() => {
    if (isAuthenticated) loadAllData();
  }, [isAuthenticated, loadAllData]);

  // Generic CRUD operations for CMS
  const createItem = async (endpoint, data) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cms/${endpoint}`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create');
      setShowAddForm(false);
      setNewItem({});
      loadAllData();
      showSuccess('Item created successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const updateItem = async (endpoint, id, data) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cms/${endpoint}/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update');
      setEditingItem(null);
      loadAllData();
      showSuccess('Item updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteItem = async (endpoint, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/cms/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to delete');
      loadAllData();
      showSuccess('Item deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Operations CRUD (Sponsors, Invites, Registrations)
  const createOpsItem = async (endpoint, data) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/${endpoint}`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create');
      setShowAddForm(false);
      setNewItem({});
      loadAllData();
      showSuccess('Item created successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const updateOpsItem = async (endpoint, id, data) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/${endpoint}/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update');
      setEditingItem(null);
      loadAllData();
      showSuccess('Item updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteOpsItem = async (endpoint, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/${endpoint}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to delete');
      loadAllData();
      showSuccess('Item deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // File upload handler
  const handleFileUpload = async (file, callback) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/upload`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      callback(`${BACKEND_URL}${data.url}`);
      showSuccess('File uploaded successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Settings update
  const updateSettings = async (updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cms/settings`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update settings');
      const data = await response.json();
      setSiteSettings(data);
      showSuccess('Settings updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Approval workflow handlers
  const handleApproveChange = async (changeId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cms/pending/${changeId}/approve`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to approve change');
      loadAllData();
      showSuccess('Change approved and applied');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRejectChange = async (changeId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cms/pending/${changeId}/reject`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: rejectNotes })
      });
      if (!response.ok) throw new Error('Failed to reject change');
      setShowRejectModal(null);
      setRejectNotes('');
      loadAllData();
      showSuccess('Change rejected');
    } catch (err) {
      setError(err.message);
    }
  };

  // Helper functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'confirmed': case 'accepted': return 'bg-green-500/20 text-green-400';
      case 'declined': return 'bg-red-500/20 text-red-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'asked': case 'invited': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'speaker': return 'bg-purple-500/20 text-purple-400';
      case 'panelist': return 'bg-cyan-500/20 text-cyan-400';
      case 'vip': return 'bg-yellow-500/20 text-yellow-400';
      case 'media': return 'bg-pink-500/20 text-pink-400';
      case 'partner': return 'bg-orange-500/20 text-orange-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const ticketTypeColor = (type) => {
    const colors = {
      standard: 'bg-blue-500/20 text-blue-400',
      vip: 'bg-amber-500/20 text-amber-400',
      virtual: 'bg-purple-500/20 text-purple-400'
    };
    return colors[type] || 'bg-muted text-muted-foreground';
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]).filter(k => k !== 'id' && !k.includes('_at'));
    const rows = data.map(item => headers.map(h => `"${item[h] || ''}"`).join(','));
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Filter registrations
  const filteredRegistrations = registrations
    .filter(reg => {
      const matchesSearch = searchQuery === '' || 
        reg.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        reg.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        reg.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (reg.company && reg.company.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesFilter = filterTicketType === 'all' || reg.ticket_type === filterTicketType;
      return matchesSearch && matchesFilter;
    });

  // Calculate totals
  const totalSponsorAmount = sponsors.reduce((sum, s) => sum + (s.amount || 0), 0);
  const confirmedSponsorAmount = sponsors.filter(s => s.status === 'confirmed').reduce((sum, s) => sum + (s.amount || 0), 0);

  // Loading state while checking session
  if (isCheckingSession) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Checking session...</p>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="glass-card rounded-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-outfit text-2xl font-bold mb-2" data-testid="admin-portal-title">
                Admin Portal
              </h1>
              <p className="text-muted-foreground text-sm">
                Manage registrations, sponsors, content, and themes
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-destructive/10 border border-destructive/30 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                <span className="text-destructive text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Username</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  data-testid="admin-password-input"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50"
                data-testid="admin-login-btn"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
                {isLoading ? 'Authenticating...' : 'Sign In'}
              </button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">
                Both admin accounts have full access to all features
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tab definitions - organized by category
  const mySubmissionsCount = mySubmissions.filter(s => s.status === 'pending').length;
  const pendingTasksCount = tasks.filter(t => t.status !== 'complete').length;
  const allTabs = [
    // Operations (accessible to all admins)
    { id: 'registrations', label: 'Registrations', icon: Ticket, count: registrations.length, category: 'ops', requiresFullAccess: false },
    { id: 'sponsors', label: 'Sponsors', icon: DollarSign, count: sponsors.length, category: 'ops', requiresFullAccess: false },
    { id: 'invites', label: 'Invites', icon: UserCheck, count: invites.length, category: 'ops', requiresFullAccess: false },
    // Tasks & Documents (accessible to all admins)
    { id: 'tasks', label: 'To-Do List', icon: ListTodo, count: pendingTasksCount, category: 'workspace', requiresFullAccess: false, highlight: pendingTasksCount > 0 },
    { id: 'documents', label: 'Documents', icon: FileText, count: documents.length, category: 'workspace', requiresFullAccess: false },
    // Content (only for full CMS admins)
    { id: 'partners', label: 'Partners', icon: Building2, count: partners.length, category: 'content', requiresFullAccess: true },
    { id: 'hero-sponsors', label: 'Sponsor Banner', icon: Globe2, count: sponsorHeroes.length, category: 'content', requiresFullAccess: true },
    { id: 'agenda', label: 'Agenda', icon: Calendar, count: agenda.length, category: 'content', requiresFullAccess: true },
    { id: 'speakers', label: 'Speakers', icon: Mic2, count: speakers.length, category: 'content', requiresFullAccess: true },
    { id: 'packages', label: 'Packages', icon: Package, count: packages.length, category: 'content', requiresFullAccess: true },
    { id: 'ticket-passes', label: 'Ticket Passes', icon: Ticket, count: ticketPasses.length, category: 'content', requiresFullAccess: true },
    // My Submissions (for cmsadmin to see their pending items)
    { id: 'my-submissions', label: 'My Submissions', icon: FileClock, count: mySubmissions.length, category: 'submissions', requiresFullAccess: true, requiresCmsAdmin: true, highlight: mySubmissionsCount > 0 },
    // Approvals (only for approver admin)
    { id: 'approvals', label: 'Pending Approvals', icon: FileClock, count: pendingCount, category: 'approvals', requiresFullAccess: true, requiresApprover: true, highlight: pendingCount > 0 },
    // Page Builder & Settings (only for full CMS admins)
    { id: 'page-builder', label: 'Page Builder', icon: Layers, count: null, category: 'settings', requiresFullAccess: true },
    { id: 'site-config', label: 'Site Configuration', icon: LayoutDashboard, count: null, category: 'settings', requiresFullAccess: true },
    { id: 'global-styling', label: 'Global Styling', icon: Palette, count: null, category: 'settings', requiresFullAccess: true },
    { id: 'theme', label: 'Theme & Design', icon: Sliders, count: null, category: 'settings', requiresFullAccess: true },
    { id: 'tenants', label: 'Tenants', icon: Globe2, count: null, category: 'settings', requiresFullAccess: true }
  ];
  
  // Filter tabs based on user role
  const tabs = allTabs.filter(tab => {
    // Approvals tab only shows for approver
    if (tab.requiresApprover && !isApprover) return false;
    // My Submissions tab only shows for cmsadmin (not approver)
    if (tab.requiresCmsAdmin && isApprover) return false;
    return !tab.requiresFullAccess || hasFullAccess;
  });

  // ============ RENDER FUNCTIONS ============

  // Registrations Tab
  const renderRegistrations = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-outfit text-xl font-semibold">Conference Registrations</h3>
        <button
          onClick={() => exportToCSV(filteredRegistrations, 'registrations')}
          className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl font-medium hover:bg-white/10"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Stats */}
      {registrationStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{registrationStats.total_registrations}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{registrationStats.by_ticket_type?.standard || 0}</p>
                <p className="text-sm text-muted-foreground">Standard</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{registrationStats.by_ticket_type?.vip || 0}</p>
                <p className="text-sm text-muted-foreground">VIP</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Ticket className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{registrationStats.by_ticket_type?.virtual || 0}</p>
                <p className="text-sm text-muted-foreground">Virtual</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Capacity Overview */}
      {registrationStats?.capacity && Object.keys(registrationStats.capacity).length > 0 && (
        <div className="glass-card rounded-xl p-6" data-testid="ticket-capacity-overview">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <Ticket className="w-4 h-4 text-primary" /> Ticket Capacity
          </h4>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(registrationStats.capacity).map(([type, data]) => (
              <div key={type} className="p-4 rounded-xl bg-background/50 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium capitalize">{type} Pass</span>
                  <span className="text-sm font-mono text-muted-foreground">${data.price}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Sold</span>
                  <span className="font-semibold">{data.sold}{data.max ? ` / ${data.max}` : ''}</span>
                </div>
                {data.max && (
                  <>
                    <div className="w-full bg-muted rounded-full h-2.5 mb-2">
                      <div
                        className={`h-2.5 rounded-full transition-all ${data.sold >= data.max ? 'bg-red-500' : data.sold >= data.max * 0.8 ? 'bg-amber-500' : 'bg-primary'}`}
                        style={{ width: `${Math.min((data.sold / data.max) * 100, 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className={`font-medium ${data.remaining <= 0 ? 'text-red-400' : data.remaining <= 5 ? 'text-amber-400' : 'text-green-400'}`}>
                        {data.remaining <= 0 ? 'SOLD OUT' : `${data.remaining} remaining`}
                      </span>
                      <span className="text-muted-foreground">{Math.round((data.sold / data.max) * 100)}% filled</span>
                    </div>
                  </>
                )}
                {data.sale_deadline && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs">
                    <Clock className="w-3 h-3 text-amber-400" />
                    <span className={new Date(data.sale_deadline + 'T23:59:59') < new Date() ? 'text-red-400 font-medium' : 'text-amber-400'}>
                      {new Date(data.sale_deadline + 'T23:59:59') < new Date() ? 'Deadline passed: ' : 'Deadline: '}
                      {new Date(data.sale_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}
                {!data.max && !data.sale_deadline && <span className="text-xs text-muted-foreground">Unlimited capacity</span>}
                {!data.max && data.sale_deadline && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs">
                    <Clock className="w-3 h-3 text-amber-400" />
                    <span className="text-amber-400">
                      Deadline: {new Date(data.sale_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-background border border-border focus:border-primary focus:outline-none"
            />
          </div>
          <select
            value={filterTicketType}
            onChange={(e) => setFilterTicketType(e.target.value)}
            className="px-4 py-2 rounded-lg bg-background border border-border focus:border-primary focus:outline-none"
          >
            <option value="all">All Ticket Types</option>
            <option value="standard">Standard</option>
            <option value="vip">VIP</option>
            <option value="virtual">Virtual</option>
          </select>
        </div>
        <div className="mt-3 text-sm text-muted-foreground">
          Showing {filteredRegistrations.length} of {registrations.length} registrations
        </div>
      </div>

      {/* Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 font-semibold">Name</th>
                <th className="text-left p-4 font-semibold">Email</th>
                <th className="text-left p-4 font-semibold">Company</th>
                <th className="text-left p-4 font-semibold">Ticket</th>
                <th className="text-left p-4 font-semibold">Registered</th>
                <th className="text-right p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistrations.length === 0 ? (
                <tr><td colSpan="6" className="text-center p-8 text-muted-foreground">No registrations found</td></tr>
              ) : (
                filteredRegistrations.map((reg) => (
                  <tr key={reg.id} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="p-4">
                      <div className="font-medium">{reg.first_name} {reg.last_name}</div>
                      {reg.role && <div className="text-sm text-muted-foreground">{reg.role}</div>}
                    </td>
                    <td className="p-4">
                      <a href={`mailto:${reg.email}`} className="text-primary hover:underline">{reg.email}</a>
                    </td>
                    <td className="p-4 text-muted-foreground">{reg.company || '—'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${ticketTypeColor(reg.ticket_type)}`}>
                        {reg.ticket_type}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(reg.registered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => deleteOpsItem('registrations', reg.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Sponsors Tab
  const renderSponsors = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-outfit text-xl font-semibold">Sponsor Pipeline</h3>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowAddForm(true); setNewItem({ status: 'asked' }); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            <Plus className="w-4 h-4" /> Add Sponsor
          </button>
          <button
            onClick={() => exportToCSV(sponsors, 'sponsors')}
            className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl font-medium hover:bg-white/10"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      {sponsorStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sponsorStats.total_sponsors}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sponsorStats.confirmed_sponsors}</p>
                <p className="text-sm text-muted-foreground">Confirmed</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(confirmedSponsorAmount)}</p>
                <p className="text-sm text-muted-foreground">Confirmed $</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(totalSponsorAmount)}</p>
                <p className="text-sm text-muted-foreground">Pipeline</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && activeTab === 'sponsors' && (
        <div className="glass-card rounded-xl p-6">
          <h4 className="font-semibold mb-4">Add New Sponsor</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" placeholder="Company Name *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Contact Person" value={newItem.contact_person || ''} onChange={(e) => setNewItem({ ...newItem, contact_person: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="email" placeholder="Email" value={newItem.email || ''} onChange={(e) => setNewItem({ ...newItem, email: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Website" value={newItem.website || ''} onChange={(e) => setNewItem({ ...newItem, website: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Amount" value={newItem.amount || ''} onChange={(e) => setNewItem({ ...newItem, amount: parseFloat(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <select value={newItem.status || 'asked'} onChange={(e) => setNewItem({ ...newItem, status: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="asked">Asked</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="declined">Declined</option>
            </select>
            <select value={newItem.tier || ''} onChange={(e) => setNewItem({ ...newItem, tier: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="">Select Tier</option>
              <option value="presenting">Presenting ($50,000)</option>
              <option value="platinum">Platinum ($25,000)</option>
              <option value="gold">Gold ($10,000)</option>
              <option value="silver">Silver ($7,500)</option>
            </select>
            <input type="text" placeholder="Owner" value={newItem.owner || ''} onChange={(e) => setNewItem({ ...newItem, owner: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Notes" value={newItem.notes || ''} onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createOpsItem('sponsors', newItem)} disabled={!newItem.name} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingItem && activeTab === 'sponsors' && (
        <div className="glass-card rounded-xl p-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Sponsor</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" placeholder="Company Name *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Contact Person" value={editingItem.contact_person || ''} onChange={(e) => setEditingItem({ ...editingItem, contact_person: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="email" placeholder="Email" value={editingItem.email || ''} onChange={(e) => setEditingItem({ ...editingItem, email: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Website" value={editingItem.website || ''} onChange={(e) => setEditingItem({ ...editingItem, website: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Amount" value={editingItem.amount || ''} onChange={(e) => setEditingItem({ ...editingItem, amount: parseFloat(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <select value={editingItem.status || 'asked'} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="asked">Asked</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="declined">Declined</option>
            </select>
            <select value={editingItem.tier || ''} onChange={(e) => setEditingItem({ ...editingItem, tier: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="">Select Tier</option>
              <option value="presenting">Presenting</option>
              <option value="platinum">Platinum</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
            </select>
            <input type="text" placeholder="Owner" value={editingItem.owner || ''} onChange={(e) => setEditingItem({ ...editingItem, owner: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Notes" value={editingItem.notes || ''} onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateOpsItem('sponsors', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium">
              <Save className="w-4 h-4" /> Update
            </button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 font-semibold">#</th>
                <th className="text-left p-4 font-semibold">Company</th>
                <th className="text-left p-4 font-semibold">Contact</th>
                <th className="text-left p-4 font-semibold">Tier</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-right p-4 font-semibold">Amount</th>
                <th className="text-left p-4 font-semibold">Owner</th>
                <th className="text-right p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sponsors.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-8 text-muted-foreground">No sponsors yet</td></tr>
              ) : (
                sponsors.map((sponsor) => (
                  <tr key={sponsor.id} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="p-4 text-muted-foreground">{sponsor.number}</td>
                    <td className="p-4 font-medium">{sponsor.name}</td>
                    <td className="p-4 text-muted-foreground">{sponsor.contact_person || '—'}</td>
                    <td className="p-4 capitalize">{sponsor.tier || '—'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadgeColor(sponsor.status)}`}>
                        {sponsor.status}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono">{formatCurrency(sponsor.amount)}</td>
                    <td className="p-4 text-muted-foreground">{sponsor.owner || '—'}</td>
                    <td className="p-4 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setEditingItem(sponsor)} className="p-2 text-primary hover:bg-primary/10 rounded">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteOpsItem('sponsors', sponsor.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {sponsors.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border bg-muted/50 font-semibold">
                  <td colSpan="5" className="p-4 text-right">Total Pipeline:</td>
                  <td className="p-4 text-right font-mono">{formatCurrency(totalSponsorAmount)}</td>
                  <td colSpan="2"></td>
                </tr>
                <tr className="bg-green-500/10 font-semibold">
                  <td colSpan="5" className="p-4 text-right text-green-400">Confirmed:</td>
                  <td className="p-4 text-right font-mono text-green-400">{formatCurrency(confirmedSponsorAmount)}</td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );

  // Invites Tab
  const renderInvites = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-outfit text-xl font-semibold">Direct Invitations</h3>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowAddForm(true); setNewItem({ status: 'invited', role_type: 'attendee' }); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium"
          >
            <Plus className="w-4 h-4" /> Add Invite
          </button>
          <button
            onClick={() => exportToCSV(invites, 'invites')}
            className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl font-medium hover:bg-white/10"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      {inviteStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inviteStats.total_invites}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inviteStats.accepted}</p>
                <p className="text-sm text-muted-foreground">Accepted</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inviteStats.pending}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inviteStats.declined}</p>
                <p className="text-sm text-muted-foreground">Declined</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && activeTab === 'invites' && (
        <div className="glass-card rounded-xl p-6">
          <h4 className="font-semibold mb-4">Add New Invite</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" placeholder="Name *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="email" placeholder="Email" value={newItem.email || ''} onChange={(e) => setNewItem({ ...newItem, email: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Organization" value={newItem.organization || ''} onChange={(e) => setNewItem({ ...newItem, organization: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <select value={newItem.role_type || 'attendee'} onChange={(e) => setNewItem({ ...newItem, role_type: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="attendee">Attendee</option>
              <option value="speaker">Speaker</option>
              <option value="panelist">Panelist</option>
              <option value="vip">VIP</option>
              <option value="media">Media</option>
              <option value="partner">Partner</option>
            </select>
            <select value={newItem.status || 'invited'} onChange={(e) => setNewItem({ ...newItem, status: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="invited">Invited</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="confirmed">Confirmed</option>
              <option value="declined">Declined</option>
            </select>
            <input type="text" placeholder="Owner" value={newItem.owner || ''} onChange={(e) => setNewItem({ ...newItem, owner: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Notes" value={newItem.notes || ''} onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })} className="md:col-span-3 px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createOpsItem('invites', newItem)} disabled={!newItem.name} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingItem && activeTab === 'invites' && (
        <div className="glass-card rounded-xl p-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Invite</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input type="text" placeholder="Name *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="email" placeholder="Email" value={editingItem.email || ''} onChange={(e) => setEditingItem({ ...editingItem, email: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Organization" value={editingItem.organization || ''} onChange={(e) => setEditingItem({ ...editingItem, organization: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <select value={editingItem.role_type || 'attendee'} onChange={(e) => setEditingItem({ ...editingItem, role_type: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="attendee">Attendee</option>
              <option value="speaker">Speaker</option>
              <option value="panelist">Panelist</option>
              <option value="vip">VIP</option>
              <option value="media">Media</option>
              <option value="partner">Partner</option>
            </select>
            <select value={editingItem.status || 'invited'} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="invited">Invited</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="confirmed">Confirmed</option>
              <option value="declined">Declined</option>
            </select>
            <input type="text" placeholder="Owner" value={editingItem.owner || ''} onChange={(e) => setEditingItem({ ...editingItem, owner: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Notes" value={editingItem.notes || ''} onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })} className="md:col-span-3 px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateOpsItem('invites', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium">
              <Save className="w-4 h-4" /> Update
            </button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 font-semibold">#</th>
                <th className="text-left p-4 font-semibold">Name</th>
                <th className="text-left p-4 font-semibold">Email</th>
                <th className="text-left p-4 font-semibold">Organization</th>
                <th className="text-left p-4 font-semibold">Role</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Owner</th>
                <th className="text-right p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-8 text-muted-foreground">No invites yet</td></tr>
              ) : (
                invites.map((invite) => (
                  <tr key={invite.id} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="p-4 text-muted-foreground">{invite.number}</td>
                    <td className="p-4 font-medium">{invite.name}</td>
                    <td className="p-4">
                      {invite.email ? <a href={`mailto:${invite.email}`} className="text-primary hover:underline">{invite.email}</a> : '—'}
                    </td>
                    <td className="p-4 text-muted-foreground">{invite.organization || '—'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getRoleBadgeColor(invite.role_type)}`}>
                        {invite.role_type}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadgeColor(invite.status)}`}>
                        {invite.status}
                      </span>
                    </td>
                    <td className="p-4 text-muted-foreground">{invite.owner || '—'}</td>
                    <td className="p-4 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setEditingItem(invite)} className="p-2 text-primary hover:bg-primary/10 rounded">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteOpsItem('invites', invite.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Partners Tab (CMS)
  const renderPartners = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-outfit text-xl font-semibold">Research Partners</h3>
        <button
          onClick={() => { setShowAddForm(true); setNewItem({ is_active: true, order: partners.length }); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium"
        >
          <Plus className="w-4 h-4" /> Add Partner
        </button>
      </div>

      {showAddForm && activeTab === 'partners' && (
        <div className="glass-card rounded-xl p-6 mb-6">
          <h4 className="font-semibold mb-4">New Partner</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Name *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Website URL" value={newItem.website || ''} onChange={(e) => setNewItem({ ...newItem, website: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Light Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL (light backgrounds)" value={newItem.logo_url_light || ''} onChange={(e) => setNewItem({ ...newItem, logo_url_light: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setNewItem({ ...newItem, logo_url_light: url }))} />
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Dark/Dim Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL (dark backgrounds)" value={newItem.logo_url_dark || ''} onChange={(e) => setNewItem({ ...newItem, logo_url_dark: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setNewItem({ ...newItem, logo_url_dark: url }))} />
                </label>
              </div>
            </div>
            <input type="text" placeholder="Description" value={newItem.description || ''} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Order" value={newItem.order || 0} onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createItem('partners', newItem)} disabled={!newItem.name} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {editingItem && activeTab === 'partners' && (
        <div className="glass-card rounded-xl p-6 mb-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Partner</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Name *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Website URL" value={editingItem.website || ''} onChange={(e) => setEditingItem({ ...editingItem, website: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Light Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL (light backgrounds)" value={editingItem.logo_url_light || ''} onChange={(e) => setEditingItem({ ...editingItem, logo_url_light: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setEditingItem({ ...editingItem, logo_url_light: url }))} />
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Dark/Dim Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL (dark backgrounds)" value={editingItem.logo_url_dark || ''} onChange={(e) => setEditingItem({ ...editingItem, logo_url_dark: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setEditingItem({ ...editingItem, logo_url_dark: url }))} />
                </label>
              </div>
            </div>
            <input type="text" placeholder="Description" value={editingItem.description || ''} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Order" value={editingItem.order || 0} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateItem('partners', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium">
              <Save className="w-4 h-4" /> Update
            </button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {partners.map((partner) => (
          <div key={partner.id} className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {partner.logo_url ? <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-contain" /> : <Building2 className="w-6 h-6 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{partner.name}</div>
              <div className="text-sm text-muted-foreground truncate">{partner.website || 'No website'}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs rounded-full ${partner.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {partner.is_active ? 'Active' : 'Hidden'}
              </span>
              <button onClick={() => { setEditingItem(partner); setShowAddForm(false); }} className="p-2 hover:bg-muted rounded-lg"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => updateItem('partners', partner.id, { is_active: !partner.is_active })} className="p-2 hover:bg-muted rounded-lg">
                {partner.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => deleteItem('partners', partner.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {partners.length === 0 && <p className="text-center text-muted-foreground py-8">No partners yet. Add your first partner above.</p>}
      </div>
    </div>
  );

  // Theme customization helpers
  const colorFields = [
    { key: 'primary', label: 'Primary', desc: 'Main brand color, buttons, links' },
    { key: 'secondary', label: 'Secondary', desc: 'Accent highlights, badges' },
    { key: 'accent', label: 'Accent', desc: 'Supporting highlights' },
    { key: 'background', label: 'Background', desc: 'Page background' },
    { key: 'foreground', label: 'Foreground', desc: 'Main text color' },
    { key: 'muted', label: 'Muted', desc: 'Subtle backgrounds, borders' },
  ];

  const getCustomColors = (mode) => {
    if (mode === 'light') return siteSettings?.custom_light || siteSettings?.light_theme || {};
    if (mode === 'dark') return siteSettings?.custom_dark || siteSettings?.dark_theme || {};
    return siteSettings?.custom_dim || siteSettings?.dim_theme || {};
  };

  const updateCustomColor = (mode, key, value) => {
    const fieldName = mode === 'light' ? 'custom_light' : mode === 'dark' ? 'custom_dark' : 'custom_dim';
    const currentColors = getCustomColors(mode);
    updateSettings({ [fieldName]: { ...currentColors, [key]: value }, use_custom_colors: true });
  };

  // Site Configuration update function
  const updateSiteConfig = async (updates) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-config`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update site configuration');
      const data = await response.json();
      setSiteConfig(data);
      showSuccess('Site configuration updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Site Configuration Tab
  const renderSiteConfig = () => {
    const configTabs = [
      { id: 'branding', label: 'Branding', icon: Image },
      { id: 'event', label: 'Event Details', icon: Calendar },
      { id: 'navigation', label: 'Navigation', icon: Menu },
      { id: 'footer', label: 'Footer', icon: Building2 },
      { id: 'legal', label: 'Legal Pages', icon: Scale }
    ];

    const config = siteConfig || {};

    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h3 className="font-outfit text-xl font-semibold">Site Configuration</h3>
            <p className="text-sm text-muted-foreground">Configure your site's branding, navigation, and content</p>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex flex-wrap gap-2">
          {configTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSiteConfigTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                siteConfigTab === tab.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'glass-card hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Branding Section */}
        {siteConfigTab === 'branding' && (
          <div className="glass-card rounded-xl p-6 space-y-6">
            <h4 className="font-semibold flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" /> Site Branding
            </h4>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Site Name</label>
                <input
                  type="text"
                  value={config.branding?.site_name || ''}
                  onChange={(e) => updateSiteConfig({ branding: { ...config.branding, site_name: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  placeholder="Your Conference Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tagline</label>
                <input
                  type="text"
                  value={config.branding?.tagline || ''}
                  onChange={(e) => updateSiteConfig({ branding: { ...config.branding, tagline: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  placeholder="Your conference tagline"
                />
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <h5 className="font-medium mb-4">Logos</h5>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Logo for Light Mode (dark logo)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={config.branding?.logos?.light_mode || ''}
                      onChange={(e) => updateSiteConfig({ 
                        branding: { 
                          ...config.branding, 
                          logos: { ...config.branding?.logos, light_mode: e.target.value } 
                        } 
                      })}
                      className="flex-1 px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                      placeholder="/logo-dark.png"
                    />
                    <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                      <Upload className="w-4 h-4" />
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => 
                          updateSiteConfig({ branding: { ...config.branding, logos: { ...config.branding?.logos, light_mode: url } } })
                        )} 
                      />
                    </label>
                  </div>
                  {config.branding?.logos?.light_mode && (
                    <div className="mt-2 p-2 bg-white rounded-lg inline-block">
                      <img src={config.branding.logos.light_mode} alt="Light mode logo" className="h-8 w-auto" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Logo for Dark/Dim Mode (light logo)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={config.branding?.logos?.dark_mode || ''}
                      onChange={(e) => updateSiteConfig({ 
                        branding: { 
                          ...config.branding, 
                          logos: { ...config.branding?.logos, dark_mode: e.target.value } 
                        } 
                      })}
                      className="flex-1 px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                      placeholder="/logo-light.png"
                    />
                    <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                      <Upload className="w-4 h-4" />
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => 
                          updateSiteConfig({ branding: { ...config.branding, logos: { ...config.branding?.logos, dark_mode: url } } })
                        )} 
                      />
                    </label>
                  </div>
                  {config.branding?.logos?.dark_mode && (
                    <div className="mt-2 p-2 bg-slate-800 rounded-lg inline-block">
                      <img src={config.branding.logos.dark_mode} alt="Dark mode logo" className="h-8 w-auto" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Details Section */}
        {siteConfigTab === 'event' && (
          <div className="glass-card rounded-xl p-6 space-y-6">
            <h4 className="font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" /> Event Details
            </h4>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Organizer Name</label>
                <input
                  type="text"
                  value={config.event?.organizer_name || ''}
                  onChange={(e) => updateSiteConfig({ event: { ...config.event, organizer_name: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Organizer Website</label>
                <input
                  type="url"
                  value={config.event?.organizer_website || ''}
                  onChange={(e) => updateSiteConfig({ event: { ...config.event, organizer_website: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Event Date</label>
                <input
                  type="date"
                  value={config.event?.event_date || ''}
                  onChange={(e) => updateSiteConfig({ event: { ...config.event, event_date: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Time</label>
                  <input
                    type="time"
                    value={config.event?.event_time || ''}
                    onChange={(e) => updateSiteConfig({ event: { ...config.event, event_time: e.target.value } })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Time</label>
                  <input
                    type="time"
                    value={config.event?.event_end_time || ''}
                    onChange={(e) => updateSiteConfig({ event: { ...config.event, event_end_time: e.target.value } })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <h5 className="font-medium mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Venue
              </h5>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Venue Name</label>
                  <input
                    type="text"
                    value={config.event?.venue?.name || ''}
                    onChange={(e) => updateSiteConfig({ 
                      event: { ...config.event, venue: { ...config.event?.venue, name: e.target.value } } 
                    })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Address</label>
                  <input
                    type="text"
                    value={config.event?.venue?.address || ''}
                    onChange={(e) => updateSiteConfig({ 
                      event: { ...config.event, venue: { ...config.event?.venue, address: e.target.value } } 
                    })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={config.event?.venue?.city || ''}
                    onChange={(e) => updateSiteConfig({ 
                      event: { ...config.event, venue: { ...config.event?.venue, city: e.target.value } } 
                    })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Country</label>
                  <input
                    type="text"
                    value={config.event?.venue?.country || ''}
                    onChange={(e) => updateSiteConfig({ 
                      event: { ...config.event, venue: { ...config.event?.venue, country: e.target.value } } 
                    })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Section */}
        {siteConfigTab === 'navigation' && (
          <div className="glass-card rounded-xl p-6 space-y-6">
            <h4 className="font-semibold flex items-center gap-2">
              <Menu className="w-5 h-5 text-primary" /> Navigation Menu
            </h4>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">Drag to reorder menu items. Toggle visibility to show/hide items.</p>
                <button
                  onClick={() => {
                    const items = config.navigation?.items || [];
                    const newItem = {
                      id: `nav-${Date.now()}`,
                      label: 'New Page',
                      path: '/new-page',
                      visible: true,
                      order: items.length,
                      external: false
                    };
                    updateSiteConfig({ 
                      navigation: { 
                        ...config.navigation, 
                        items: [...items, newItem] 
                      } 
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium"
                >
                  <Plus className="w-4 h-4" /> Add Menu Item
                </button>
              </div>

              <div className="space-y-2">
                {(config.navigation?.items || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, index) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
                    <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => {
                        const items = [...(config.navigation?.items || [])];
                        const idx = items.findIndex(i => i.id === item.id);
                        items[idx] = { ...items[idx], label: e.target.value };
                        updateSiteConfig({ navigation: { ...config.navigation, items } });
                      }}
                      className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                      placeholder="Label"
                    />
                    <input
                      type="text"
                      value={item.path}
                      onChange={(e) => {
                        const items = [...(config.navigation?.items || [])];
                        const idx = items.findIndex(i => i.id === item.id);
                        items[idx] = { ...items[idx], path: e.target.value };
                        updateSiteConfig({ navigation: { ...config.navigation, items } });
                      }}
                      className="w-40 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                      placeholder="/path"
                    />
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={item.visible !== false}
                        onChange={(e) => {
                          const items = [...(config.navigation?.items || [])];
                          const idx = items.findIndex(i => i.id === item.id);
                          items[idx] = { ...items[idx], visible: e.target.checked };
                          updateSiteConfig({ navigation: { ...config.navigation, items } });
                        }}
                        className="rounded"
                      />
                      Visible
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={item.external === true}
                        onChange={(e) => {
                          const items = [...(config.navigation?.items || [])];
                          const idx = items.findIndex(i => i.id === item.id);
                          items[idx] = { ...items[idx], external: e.target.checked };
                          updateSiteConfig({ navigation: { ...config.navigation, items } });
                        }}
                        className="rounded"
                      />
                      External
                    </label>
                    <button
                      onClick={() => {
                        const items = (config.navigation?.items || []).filter(i => i.id !== item.id);
                        updateSiteConfig({ navigation: { ...config.navigation, items } });
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <h5 className="font-medium mb-4">Register Button</h5>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={config.navigation?.show_register_button !== false}
                      onChange={(e) => updateSiteConfig({ 
                        navigation: { ...config.navigation, show_register_button: e.target.checked } 
                      })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">Show Register Button</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Button Text</label>
                  <input
                    type="text"
                    value={config.navigation?.register_button_text || 'Register'}
                    onChange={(e) => updateSiteConfig({ 
                      navigation: { ...config.navigation, register_button_text: e.target.value } 
                    })}
                    className="w-full px-4 py-2 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Button Path</label>
                  <input
                    type="text"
                    value={config.navigation?.register_button_path || '/signup'}
                    onChange={(e) => updateSiteConfig({ 
                      navigation: { ...config.navigation, register_button_path: e.target.value } 
                    })}
                    className="w-full px-4 py-2 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Section */}
        {siteConfigTab === 'footer' && (
          <div className="glass-card rounded-xl p-6 space-y-6">
            <h4 className="font-semibold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" /> Footer Settings
            </h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">About Text</label>
                <textarea
                  value={config.footer?.about_text || ''}
                  onChange={(e) => updateSiteConfig({ footer: { ...config.footer, about_text: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  rows={3}
                  placeholder="Short description for the footer..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Copyright Text</label>
                <input
                  type="text"
                  value={config.footer?.copyright_text || ''}
                  onChange={(e) => updateSiteConfig({ footer: { ...config.footer, copyright_text: e.target.value } })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  placeholder="© 2026 Your Company. All rights reserved."
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Contact Email</label>
                  <input
                    type="email"
                    value={config.footer?.contact_email || ''}
                    onChange={(e) => updateSiteConfig({ footer: { ...config.footer, contact_email: e.target.value } })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    value={config.footer?.contact_phone || ''}
                    onChange={(e) => updateSiteConfig({ footer: { ...config.footer, contact_phone: e.target.value } })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <div className="flex justify-between items-center mb-4">
                <h5 className="font-medium">Social Links</h5>
                <button
                  onClick={() => {
                    const links = config.footer?.social_links || [];
                    updateSiteConfig({ 
                      footer: { 
                        ...config.footer, 
                        social_links: [...links, { platform: 'linkedin', url: '', visible: true }] 
                      } 
                    });
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 text-primary rounded-lg text-sm"
                >
                  <Plus className="w-4 h-4" /> Add Social Link
                </button>
              </div>
              <div className="space-y-2">
                {(config.footer?.social_links || []).map((link, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
                    <select
                      value={link.platform}
                      onChange={(e) => {
                        const links = [...(config.footer?.social_links || [])];
                        links[index] = { ...links[index], platform: e.target.value };
                        updateSiteConfig({ footer: { ...config.footer, social_links: links } });
                      }}
                      className="px-3 py-2 rounded-lg bg-background border border-border text-sm"
                    >
                      <option value="linkedin">LinkedIn</option>
                      <option value="twitter">Twitter/X</option>
                      <option value="facebook">Facebook</option>
                      <option value="instagram">Instagram</option>
                      <option value="youtube">YouTube</option>
                    </select>
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => {
                        const links = [...(config.footer?.social_links || [])];
                        links[index] = { ...links[index], url: e.target.value };
                        updateSiteConfig({ footer: { ...config.footer, social_links: links } });
                      }}
                      className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                      placeholder="https://..."
                    />
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={link.visible !== false}
                        onChange={(e) => {
                          const links = [...(config.footer?.social_links || [])];
                          links[index] = { ...links[index], visible: e.target.checked };
                          updateSiteConfig({ footer: { ...config.footer, social_links: links } });
                        }}
                        className="rounded"
                      />
                      Show
                    </label>
                    <button
                      onClick={() => {
                        const links = (config.footer?.social_links || []).filter((_, i) => i !== index);
                        updateSiteConfig({ footer: { ...config.footer, social_links: links } });
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Legal Pages Section */}
        {siteConfigTab === 'legal' && (
          <div className="glass-card rounded-xl p-6 space-y-6">
            <h4 className="font-semibold flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" /> Legal Pages
            </h4>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Privacy Policy</label>
                <textarea
                  value={config.legal?.privacy_policy?.content || ''}
                  onChange={(e) => updateSiteConfig({ 
                    legal: { 
                      ...config.legal, 
                      privacy_policy: { 
                        ...config.legal?.privacy_policy, 
                        title: 'Privacy Policy',
                        slug: 'privacy-policy',
                        content: e.target.value,
                        last_updated: new Date().toISOString()
                      } 
                    } 
                  })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none font-mono text-sm"
                  rows={10}
                  placeholder="<h2>Privacy Policy</h2><p>Your privacy policy content (HTML supported)...</p>"
                />
                <p className="text-xs text-muted-foreground mt-1">HTML formatting is supported. Use &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt; tags.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Terms of Service</label>
                <textarea
                  value={config.legal?.terms_of_service?.content || ''}
                  onChange={(e) => updateSiteConfig({ 
                    legal: { 
                      ...config.legal, 
                      terms_of_service: { 
                        ...config.legal?.terms_of_service, 
                        title: 'Terms of Service',
                        slug: 'terms-of-service',
                        content: e.target.value,
                        last_updated: new Date().toISOString()
                      } 
                    } 
                  })}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none font-mono text-sm"
                  rows={10}
                  placeholder="<h2>Terms of Service</h2><p>Your terms content (HTML supported)...</p>"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Page Builder Tab
  // Page CRUD operations
  const createNewPage = async () => {
    if (!newPageData.title || !newPageData.slug) {
      setError('Page title and URL slug are required');
      return;
    }
    
    // Sanitize slug
    const slug = newPageData.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pages`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          title: newPageData.title,
          description: newPageData.description,
          blocks: [],
          seo: {
            meta_title: newPageData.title,
            meta_description: newPageData.description
          }
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create page');
      }
      
      const newPage = await response.json();
      setAllPages([...allPages, newPage]);
      setShowCreatePage(false);
      setNewPageData({ title: '', slug: '', description: '' });
      setSelectedPage(slug);
      showSuccess(`Page "${newPageData.title}" created successfully!`);
      
      // Ask if user wants to add to navigation
      if (window.confirm('Would you like to add this page to the navigation menu?')) {
        const navItems = siteConfig?.navigation?.items || [];
        const newNavItem = {
          id: `nav-${slug}`,
          label: newPageData.title,
          path: `/${slug}`,
          visible: true,
          order: navItems.length,
          external: false
        };
        await updateSiteConfig({
          navigation: {
            ...siteConfig?.navigation,
            items: [...navItems, newNavItem]
          }
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const deletePage = async (slug) => {
    if (slug === 'home') {
      setError('Cannot delete the homepage');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the page "${slug}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/pages/${slug}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (!response.ok) throw new Error('Failed to delete page');
      
      setAllPages(allPages.filter(p => p.slug !== slug));
      if (selectedPage === slug) setSelectedPage('home');
      showSuccess('Page deleted successfully');
      
      // Remove from navigation if present
      const navItems = siteConfig?.navigation?.items || [];
      const updatedNavItems = navItems.filter(item => item.path !== `/${slug}`);
      if (updatedNavItems.length !== navItems.length) {
        await updateSiteConfig({
          navigation: {
            ...siteConfig?.navigation,
            items: updatedNavItems
          }
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const renderPageBuilder = () => {
    // Default pages that always exist
    const defaultPages = [
      { slug: 'home', title: 'Homepage', isDefault: true },
      { slug: 'speakers', title: 'Speakers Page', isDefault: true },
      { slug: 'agenda', title: 'Agenda Page', isDefault: true },
      { slug: 'experience', title: 'Experience Page', isDefault: true },
      { slug: 'sponsorship', title: 'Partnership Page', isDefault: true },
      { slug: 'signup', title: 'Registration Page', isDefault: true },
    ];
    
    // Merge default pages with custom pages from database
    const customPages = allPages.filter(p => !defaultPages.some(dp => dp.slug === p.slug));
    const allAvailablePages = [
      ...defaultPages.map(dp => {
        const dbPage = allPages.find(p => p.slug === dp.slug);
        return { ...dp, isDeactivated: dbPage ? dbPage.is_published === false : false };
      }),
      ...customPages.map(p => ({ slug: p.slug, title: p.title, isDefault: false, isDeactivated: p.is_published === false }))
    ];

    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h3 className="font-outfit text-xl font-semibold">Visual Page Builder</h3>
            <p className="text-sm text-muted-foreground">Drag and drop blocks to build your pages</p>
          </div>
          <button
            onClick={() => setShowCreatePage(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium transition-all hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4" />
            Create New Page
          </button>
        </div>

        {/* Page Selector */}
        <div className="glass-card rounded-xl p-4">
          <label className="block text-sm font-medium mb-3">Select Page to Edit</label>
          <div className="flex flex-wrap gap-2">
            {allAvailablePages.map(page => (
              <div key={page.slug} className="flex items-center gap-1">
                <button
                  onClick={() => setSelectedPage(page.slug)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    selectedPage === page.slug 
                      ? 'bg-primary text-primary-foreground' 
                      : page.isDeactivated ? 'glass-card opacity-50 line-through' : 'glass-card hover:bg-white/10'
                  }`}
                >
                  {page.title}
                </button>
                <button
                  onClick={() => {
                    fetch(`${BACKEND_URL}/api/pages/${page.slug}/toggle-publish`, { method: 'PUT', headers: getAuthHeader() })
                      .then(res => res.ok ? res.json() : Promise.reject('Failed'))
                      .then(data => {
                        showSuccess(`${page.title} ${data.is_published ? 'activated' : 'deactivated'}`);
                        return fetch(`${BACKEND_URL}/api/pages`, { headers: getAuthHeader() });
                      })
                      .then(res => res.ok ? res.json() : [])
                      .then(pages => setAllPages(pages))
                      .catch(err => setError(String(err)));
                  }}
                  className={`p-1.5 rounded-lg transition-colors ${page.isDeactivated ? 'text-green-400 hover:bg-green-500/20' : 'text-amber-400 hover:bg-amber-500/20'}`}
                  title={page.isDeactivated ? 'Activate page' : 'Deactivate page'}
                >
                  {page.isDeactivated ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                {!page.isDefault && (
                  <button
                    onClick={() => deletePage(page.slug)}
                    className="p-1.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    title="Delete page"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Page Builder Component */}
        <PageBuilder 
          pageSlug={selectedPage} 
          credentials={credentials}
          onSave={() => showSuccess('Page saved successfully!')}
        />

        {/* Preview Link */}
        <div className="glass-card rounded-xl p-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Preview your changes on the live site
          </div>
          <a
            href={selectedPage === 'home' ? '/' : `/${selectedPage}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-xl text-sm hover:bg-primary/30 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Open Preview
          </a>
        </div>

        {/* Create Page Modal */}
        {showCreatePage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="glass-card rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">Create New Page</h3>
                <button onClick={() => setShowCreatePage(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Page Title *</label>
                  <input
                    type="text"
                    value={newPageData.title}
                    onChange={(e) => {
                      setNewPageData({
                        ...newPageData,
                        title: e.target.value,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
                      });
                    }}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                    placeholder="e.g., About Us"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">URL Slug *</label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">/</span>
                    <input
                      type="text"
                      value={newPageData.slug}
                      onChange={(e) => setNewPageData({ ...newPageData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className="flex-1 px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                      placeholder="about-us"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">URL-friendly slug (lowercase, no spaces)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description (optional)</label>
                  <textarea
                    value={newPageData.description}
                    onChange={(e) => setNewPageData({ ...newPageData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                    rows={3}
                    placeholder="Brief description for SEO..."
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                <button
                  onClick={() => setShowCreatePage(false)}
                  className="px-4 py-2 rounded-xl hover:bg-white/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createNewPage}
                  disabled={!newPageData.title || !newPageData.slug}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Create Page
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Theme Settings Tab
  const renderThemeSettings = () => (
    <div className="space-y-8">
      <h3 className="font-outfit text-xl font-semibold">Theme & Design Settings</h3>

      {/* Color Palette */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-semibold">Color Palette</h4>
          <button onClick={() => setShowCustomColors(!showCustomColors)} className="flex items-center gap-2 text-sm text-primary hover:underline">
            <Sliders className="w-4 h-4" />
            {showCustomColors ? 'Hide Custom Colors' : 'Customize Colors'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {palettes.map((palette) => (
            <button
              key={palette.id}
              onClick={() => {
                updateSettings({ active_palette: palette.id, use_custom_colors: false });
                showSuccess(`Theme "${palette.name}" applied successfully!`);
              }}
              className={`p-4 rounded-xl border-2 transition-all ${siteSettings?.active_palette === palette.id && !siteSettings?.use_custom_colors ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-primary/50'}`}
              style={palette.background ? { backgroundColor: palette.background } : {}}
              data-testid={`palette-${palette.id}`}
            >
              <div className="flex gap-1 mb-2 justify-center">
                <div className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: palette.colors.primary }} title="Primary" />
                <div className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: palette.colors.secondary }} title="Secondary" />
                <div className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: palette.colors.accent }} title="Accent" />
              </div>
              {palette.background && (
                <div className="flex gap-1 mb-2 justify-center">
                  <div className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: palette.background }} title="Background" />
                  <div className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: palette.card_bg }} title="Card BG" />
                  <div className="w-4 h-4 rounded border border-white/20" style={{ backgroundColor: palette.text }} title="Text" />
                </div>
              )}
              <p className="text-xs text-center font-medium" style={palette.text ? { color: palette.text } : {}}>{palette.name}</p>
              {palette.description && (
                <p className="text-[10px] text-center opacity-70 mt-1" style={palette.text ? { color: palette.text } : {}}>{palette.description}</p>
              )}
            </button>
          ))}
        </div>
        
        {showCustomColors && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm font-medium">Edit colors for:</span>
              <div className="flex gap-2">
                {['light', 'dark', 'dim'].map((mode) => (
                  <button key={mode} onClick={() => setCustomColorMode(mode)} className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${customColorMode === mode ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
                    {mode} Mode
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {colorFields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <label className="text-sm font-medium block">{field.label}</label>
                  <div className="flex gap-2">
                    <input type="color" value={getCustomColors(customColorMode)[field.key] || '#0EA5E9'} onChange={(e) => updateCustomColor(customColorMode, field.key, e.target.value)} className="w-12 h-10 rounded-lg cursor-pointer border-0" />
                    <input type="text" value={getCustomColors(customColorMode)[field.key] || ''} onChange={(e) => updateCustomColor(customColorMode, field.key, e.target.value)} placeholder="#000000" className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono" />
                  </div>
                  <p className="text-xs text-muted-foreground">{field.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Typography */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4 flex items-center gap-2"><Type className="w-5 h-5" /> Typography</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Heading Font</label>
            <div className="grid grid-cols-2 gap-2">
              {fonts.heading?.map((font) => (
                <button key={font.id} onClick={() => updateSettings({ heading_font: font.id })} className={`p-3 rounded-xl text-left transition-all ${siteSettings?.heading_font === font.id ? 'bg-primary/20 border-2 border-primary' : 'bg-muted/50 border-2 border-transparent hover:border-primary/30'}`}>
                  <p className="font-semibold text-sm">{font.name}</p>
                  <p className="text-xs text-muted-foreground">{font.preview}</p>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Body Font</label>
            <div className="grid grid-cols-2 gap-2">
              {fonts.body?.map((font) => (
                <button key={font.id} onClick={() => updateSettings({ body_font: font.id })} className={`p-3 rounded-xl text-left transition-all ${siteSettings?.body_font === font.id ? 'bg-primary/20 border-2 border-primary' : 'bg-muted/50 border-2 border-transparent hover:border-primary/30'}`}>
                  <p className="font-semibold text-sm">{font.name}</p>
                  <p className="text-xs text-muted-foreground">{font.preview}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6">
          <label className="block text-sm font-medium mb-2">Font Scale</label>
          <div className="flex gap-2">
            {fontScales.map((scale) => (
              <button key={scale.id} onClick={() => updateSettings({ font_scale: scale.id })} className={`flex-1 p-3 rounded-xl text-center transition-all ${siteSettings?.font_scale === scale.id ? 'bg-primary text-primary-foreground' : 'bg-muted/50 hover:bg-muted'}`}>
                <p className="font-semibold text-sm">{scale.name}</p>
                <p className="text-xs opacity-80">{scale.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Icon Library */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4">Icon Library</h4>
        <div className="grid md:grid-cols-3 gap-3">
          {iconLibraries.map((lib) => (
            <button key={lib.id} onClick={() => updateSettings({ icon_library: lib.id })} className={`p-4 rounded-xl text-left transition-all ${siteSettings?.icon_library === lib.id ? 'bg-primary/20 border-2 border-primary' : 'bg-muted/50 border-2 border-transparent hover:border-primary/30'}`}>
              <p className="font-semibold text-sm">{lib.name}</p>
              <p className="text-xs text-muted-foreground">{lib.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Background */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4">Background Image</h4>
        <div className="flex gap-4 items-start">
          <div className="w-32 h-20 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
            {siteSettings?.background_image_url ? <img src={siteSettings.background_image_url} alt="Background" className="w-full h-full object-cover" /> : <Image className="w-8 h-8 text-muted-foreground" />}
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex gap-2">
              <input type="text" placeholder="Background image URL" value={siteSettings?.background_image_url || ''} onChange={(e) => updateSettings({ background_image_url: e.target.value })} className="flex-1 px-4 py-2 rounded-xl bg-background border border-border" />
              <label className="flex items-center gap-2 px-4 py-2 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                <Upload className="w-4 h-4" />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => updateSettings({ background_image_url: url }))} />
              </label>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Opacity: {siteSettings?.background_opacity || 0.5}</label>
              <input type="range" min="0" max="1" step="0.05" value={siteSettings?.background_opacity || 0.5} onChange={(e) => updateSettings({ background_opacity: parseFloat(e.target.value) })} className="w-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Background */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4">Interactive Background</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {interactivePresets.map((preset) => (
            <button key={preset.id} onClick={() => updateSettings({ interactive: { ...siteSettings?.interactive, preset: preset.id } })} className={`p-4 rounded-xl border-2 transition-all ${siteSettings?.interactive?.preset === preset.id ? 'border-primary bg-primary/10' : 'border-transparent hover:border-primary/50 bg-muted/50'}`}>
              <p className="text-sm font-medium mb-1">{preset.name}</p>
              <p className="text-xs text-muted-foreground">{preset.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
        <p className="text-sm text-green-400"><strong>✓ Auto-Save Enabled:</strong> Theme changes are saved immediately when you click a palette. Refresh the public site to see your changes.</p>
      </div>
    </div>
  );

  // Placeholder for remaining CMS tabs (simplified versions)
  const renderSponsorHeroes = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-outfit text-xl font-semibold">Sponsor Hero Banner</h3>
        <button onClick={() => { setShowAddForm(true); setEditingItem(null); setNewItem({ is_active: true, order: sponsorHeroes.length }); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium">
          <Plus className="w-4 h-4" /> Add Sponsor
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && activeTab === 'hero-sponsors' && (
        <div className="glass-card rounded-xl p-6 mb-6">
          <h4 className="font-semibold mb-4">New Sponsor Hero</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Company Name *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Website URL" value={newItem.website || ''} onChange={(e) => setNewItem({ ...newItem, website: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Light Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL" value={newItem.logo_url_light || ''} onChange={(e) => setNewItem({ ...newItem, logo_url_light: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setNewItem({ ...newItem, logo_url_light: url }))} />
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Dark/Dim Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL" value={newItem.logo_url_dark || ''} onChange={(e) => setNewItem({ ...newItem, logo_url_dark: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setNewItem({ ...newItem, logo_url_dark: url }))} />
                </label>
              </div>
            </div>
            <select value={newItem.tier || ''} onChange={(e) => setNewItem({ ...newItem, tier: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="">Select Tier</option>
              <option value="presenting">Presenting</option>
              <option value="platinum">Platinum</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
            </select>
            <input type="number" placeholder="Order" value={newItem.order || 0} onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createItem('sponsor-heroes', newItem)} disabled={!newItem.name} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingItem && activeTab === 'hero-sponsors' && (
        <div className="glass-card rounded-xl p-6 mb-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Sponsor Hero</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Company Name *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Website URL" value={editingItem.website || ''} onChange={(e) => setEditingItem({ ...editingItem, website: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Light Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL" value={editingItem.logo_url_light || ''} onChange={(e) => setEditingItem({ ...editingItem, logo_url_light: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setEditingItem({ ...editingItem, logo_url_light: url }))} />
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Logo for Dark/Dim Mode</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Logo URL" value={editingItem.logo_url_dark || ''} onChange={(e) => setEditingItem({ ...editingItem, logo_url_dark: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setEditingItem({ ...editingItem, logo_url_dark: url }))} />
                </label>
              </div>
            </div>
            <select value={editingItem.tier || ''} onChange={(e) => setEditingItem({ ...editingItem, tier: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="">Select Tier</option>
              <option value="presenting">Presenting</option>
              <option value="platinum">Platinum</option>
              <option value="gold">Gold</option>
              <option value="silver">Silver</option>
            </select>
            <input type="number" placeholder="Order" value={editingItem.order || 0} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateItem('sponsor-heroes', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"><Save className="w-4 h-4" /> Update</button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {sponsorHeroes.map((sponsor) => (
          <div key={sponsor.id} className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className="w-16 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {sponsor.logo_url ? <img src={sponsor.logo_url} alt={sponsor.name} className="w-full h-full object-contain" /> : <Globe2 className="w-6 h-6 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{sponsor.name}</div>
              <div className="text-sm text-muted-foreground capitalize">{sponsor.tier || 'No tier'}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs rounded-full ${sponsor.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{sponsor.is_active ? 'Visible' : 'Hidden'}</span>
              <button onClick={() => { setEditingItem(sponsor); setShowAddForm(false); }} className="p-2 hover:bg-muted rounded-lg"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => updateItem('sponsor-heroes', sponsor.id, { is_active: !sponsor.is_active })} className="p-2 hover:bg-muted rounded-lg">{sponsor.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              <button onClick={() => deleteItem('sponsor-heroes', sponsor.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {sponsorHeroes.length === 0 && <p className="text-center text-muted-foreground py-8">No sponsor heroes yet.</p>}
      </div>
    </div>
  );

  const renderAgenda = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-outfit text-xl font-semibold">Conference Agenda</h3>
        <button onClick={() => { setShowAddForm(true); setEditingItem(null); setNewItem({ is_active: true, order: agenda.length, speakers: [], panelists: [], session_type: 'session' }); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium">
          <Plus className="w-4 h-4" /> Add Session
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && activeTab === 'agenda' && (
        <div className="glass-card rounded-xl p-6 mb-6">
          <h4 className="font-semibold mb-4">New Agenda Item</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Time (e.g., 09:00 - 09:30) *" value={newItem.time || ''} onChange={(e) => setNewItem({ ...newItem, time: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Title *" value={newItem.title || ''} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <select value={newItem.session_type || 'session'} onChange={(e) => setNewItem({ ...newItem, session_type: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="session">Session</option>
              <option value="keynote">Keynote</option>
              <option value="panel">Panel</option>
              <option value="break">Break</option>
              <option value="networking">Networking</option>
            </select>
            <input type="text" placeholder="Location" value={newItem.location || ''} onChange={(e) => setNewItem({ ...newItem, location: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Speakers (comma separated)" value={newItem.speakers?.join(', ') || ''} onChange={(e) => setNewItem({ ...newItem, speakers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" />
            <textarea placeholder="Description" value={newItem.description || ''} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={3} />
            <input type="number" placeholder="Order" value={newItem.order || 0} onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createItem('agenda', newItem)} disabled={!newItem.time || !newItem.title} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingItem && activeTab === 'agenda' && (
        <div className="glass-card rounded-xl p-6 mb-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Agenda Item</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Time (e.g., 09:00 - 09:30) *" value={editingItem.time || ''} onChange={(e) => setEditingItem({ ...editingItem, time: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Title *" value={editingItem.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <select value={editingItem.session_type || 'session'} onChange={(e) => setEditingItem({ ...editingItem, session_type: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">
              <option value="session">Session</option>
              <option value="keynote">Keynote</option>
              <option value="panel">Panel</option>
              <option value="break">Break</option>
              <option value="networking">Networking</option>
            </select>
            <input type="text" placeholder="Location" value={editingItem.location || ''} onChange={(e) => setEditingItem({ ...editingItem, location: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Speakers (comma separated)" value={editingItem.speakers?.join(', ') || ''} onChange={(e) => setEditingItem({ ...editingItem, speakers: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" />
            <textarea placeholder="Description" value={editingItem.description || ''} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={3} />
            <input type="number" placeholder="Order" value={editingItem.order || 0} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateItem('agenda', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium">
              <Save className="w-4 h-4" /> Update
            </button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {agenda.map((item) => (
          <div key={item.id} className="glass-card rounded-xl p-4">
            <div className="flex items-start gap-4">
              <div className="w-28 flex-shrink-0"><span className="text-sm font-mono text-primary">{item.time}</span></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{item.title}</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${item.session_type === 'keynote' ? 'bg-purple-500/20 text-purple-400' : item.session_type === 'panel' ? 'bg-blue-500/20 text-blue-400' : item.session_type === 'break' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-muted text-muted-foreground'}`}>{item.session_type}</span>
                </div>
                {item.speakers?.length > 0 && <p className="text-sm text-muted-foreground">Speakers: {item.speakers.join(', ')}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded-full ${item.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{item.is_active ? 'Active' : 'Hidden'}</span>
                <button onClick={() => { setEditingItem(item); setShowAddForm(false); }} className="p-2 hover:bg-muted rounded-lg"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => updateItem('agenda', item.id, { is_active: !item.is_active })} className="p-2 hover:bg-muted rounded-lg">{item.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                <button onClick={() => deleteItem('agenda', item.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {agenda.length === 0 && <p className="text-center text-muted-foreground py-8">No agenda items yet.</p>}
      </div>
    </div>
  );

  const renderSpeakers = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-outfit text-xl font-semibold">Conference Speakers</h3>
        <button onClick={() => { setShowAddForm(true); setEditingItem(null); setNewItem({ is_active: true, order: speakers.length, is_featured: false }); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium">
          <Plus className="w-4 h-4" /> Add Speaker
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && activeTab === 'speakers' && (
        <div className="glass-card rounded-xl p-6 mb-6">
          <h4 className="font-semibold mb-4">New Speaker</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Name *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Title/Role" value={newItem.title || ''} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Organization" value={newItem.organization || ''} onChange={(e) => setNewItem({ ...newItem, organization: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Topic" value={newItem.topic || ''} onChange={(e) => setNewItem({ ...newItem, topic: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Photo</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Photo URL" value={newItem.photo_url || ''} onChange={(e) => setNewItem({ ...newItem, photo_url: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setNewItem({ ...newItem, photo_url: url }))} />
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image Focus Point</label>
              <select value={newItem.image_position || 'center top'} onChange={(e) => setNewItem({ ...newItem, image_position: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-background border border-border">
                <option value="center top">Top (show head)</option>
                <option value="center 20%">Upper (20%)</option>
                <option value="center center">Center</option>
                <option value="center 80%">Lower (80%)</option>
                <option value="center bottom">Bottom</option>
              </select>
            </div>
            <textarea placeholder="Bio" value={newItem.bio || ''} onChange={(e) => setNewItem({ ...newItem, bio: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={3} />
            <input type="text" placeholder="LinkedIn URL" value={newItem.linkedin || ''} onChange={(e) => setNewItem({ ...newItem, linkedin: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Twitter Handle" value={newItem.twitter || ''} onChange={(e) => setNewItem({ ...newItem, twitter: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Session Time" value={newItem.session_time || ''} onChange={(e) => setNewItem({ ...newItem, session_time: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newItem.is_featured || false} onChange={(e) => setNewItem({ ...newItem, is_featured: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Featured Speaker</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createItem('speakers', newItem)} disabled={!newItem.name} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingItem && activeTab === 'speakers' && (
        <div className="glass-card rounded-xl p-6 mb-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Speaker</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Name *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Title/Role" value={editingItem.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Organization" value={editingItem.organization || ''} onChange={(e) => setEditingItem({ ...editingItem, organization: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Topic" value={editingItem.topic || ''} onChange={(e) => setEditingItem({ ...editingItem, topic: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Photo</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Photo URL" value={editingItem.photo_url || ''} onChange={(e) => setEditingItem({ ...editingItem, photo_url: e.target.value })} className="flex-1 px-4 py-3 rounded-xl bg-background border border-border" />
                <label className="flex items-center gap-2 px-4 py-3 bg-muted rounded-xl cursor-pointer hover:bg-muted/80">
                  <Upload className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], (url) => setEditingItem({ ...editingItem, photo_url: url }))} />
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Image Focus Point</label>
              <select value={editingItem.image_position || 'center top'} onChange={(e) => setEditingItem({ ...editingItem, image_position: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-background border border-border">
                <option value="center top">Top (show head)</option>
                <option value="center 20%">Upper (20%)</option>
                <option value="center center">Center</option>
                <option value="center 80%">Lower (80%)</option>
                <option value="center bottom">Bottom</option>
              </select>
            </div>
            <textarea placeholder="Bio" value={editingItem.bio || ''} onChange={(e) => setEditingItem({ ...editingItem, bio: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={3} />
            <input type="text" placeholder="LinkedIn URL" value={editingItem.linkedin || ''} onChange={(e) => setEditingItem({ ...editingItem, linkedin: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Twitter Handle" value={editingItem.twitter || ''} onChange={(e) => setEditingItem({ ...editingItem, twitter: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Session Time" value={editingItem.session_time || ''} onChange={(e) => setEditingItem({ ...editingItem, session_time: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingItem.is_featured || false} onChange={(e) => setEditingItem({ ...editingItem, is_featured: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Featured Speaker</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateItem('speakers', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium">
              <Save className="w-4 h-4" /> Update
            </button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {speakers.map((speaker) => (
          <div key={speaker.id} className="glass-card rounded-xl p-4 flex gap-4">
            <div className="w-20 h-20 rounded-xl bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {speaker.photo_url ? <img src={speaker.photo_url} alt={speaker.name} className="w-full h-full object-cover" /> : <Mic2 className="w-8 h-8 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium truncate">{speaker.name}</span>
                {speaker.is_featured && <span className="px-2 py-0.5 text-xs bg-secondary/20 text-secondary rounded-full">Featured</span>}
              </div>
              <p className="text-sm text-muted-foreground truncate">{speaker.title}</p>
              <p className="text-sm text-muted-foreground truncate">{speaker.organization}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => { setEditingItem(speaker); setShowAddForm(false); }} className="p-2 hover:bg-muted rounded-lg"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => updateItem('speakers', speaker.id, { is_active: !speaker.is_active })} className="p-2 hover:bg-muted rounded-lg">{speaker.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              <button onClick={() => deleteItem('speakers', speaker.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {speakers.length === 0 && <p className="col-span-2 text-center text-muted-foreground py-8">No speakers yet.</p>}
      </div>
    </div>
  );

  const renderPackages = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-outfit text-xl font-semibold">Sponsorship Packages</h3>
        <button onClick={() => { setShowAddForm(true); setEditingItem(null); setNewItem({ is_active: true, order: packages.length, benefits: [], is_featured: false, is_exclusive: false }); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium">
          <Plus className="w-4 h-4" /> Add Package
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && activeTab === 'packages' && (
        <div className="glass-card rounded-xl p-6 mb-6">
          <h4 className="font-semibold mb-4">New Sponsorship Package</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Package Name *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Price" value={newItem.price || ''} onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Price Display (e.g., $50,000)" value={newItem.price_display || ''} onChange={(e) => setNewItem({ ...newItem, price_display: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Tagline" value={newItem.tagline || ''} onChange={(e) => setNewItem({ ...newItem, tagline: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <textarea placeholder="Description" value={newItem.description || ''} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={2} />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Benefits (one per line)</label>
              <textarea placeholder="Logo on website&#10;Speaking opportunity&#10;VIP access" value={newItem.benefits?.join('\n') || ''} onChange={(e) => setNewItem({ ...newItem, benefits: e.target.value.split('\n').filter(Boolean) })} className="w-full px-4 py-3 rounded-xl bg-background border border-border" rows={4} />
            </div>
            <input type="number" placeholder="Limit (leave empty for unlimited)" value={newItem.limit || ''} onChange={(e) => setNewItem({ ...newItem, limit: parseInt(e.target.value) || null })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Order" value={newItem.order || 0} onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newItem.is_featured || false} onChange={(e) => setNewItem({ ...newItem, is_featured: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newItem.is_exclusive || false} onChange={(e) => setNewItem({ ...newItem, is_exclusive: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Exclusive</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createItem('packages', newItem)} disabled={!newItem.name} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingItem && activeTab === 'packages' && (
        <div className="glass-card rounded-xl p-6 mb-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Sponsorship Package</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Package Name *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Price" value={editingItem.price || ''} onChange={(e) => setEditingItem({ ...editingItem, price: parseFloat(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Price Display (e.g., $50,000)" value={editingItem.price_display || ''} onChange={(e) => setEditingItem({ ...editingItem, price_display: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Tagline" value={editingItem.tagline || ''} onChange={(e) => setEditingItem({ ...editingItem, tagline: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <textarea placeholder="Description" value={editingItem.description || ''} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={2} />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Benefits (one per line)</label>
              <textarea placeholder="Logo on website&#10;Speaking opportunity&#10;VIP access" value={editingItem.benefits?.join('\n') || ''} onChange={(e) => setEditingItem({ ...editingItem, benefits: e.target.value.split('\n').filter(Boolean) })} className="w-full px-4 py-3 rounded-xl bg-background border border-border" rows={4} />
            </div>
            <input type="number" placeholder="Limit (leave empty for unlimited)" value={editingItem.limit || ''} onChange={(e) => setEditingItem({ ...editingItem, limit: parseInt(e.target.value) || null })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Order" value={editingItem.order || 0} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingItem.is_featured || false} onChange={(e) => setEditingItem({ ...editingItem, is_featured: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingItem.is_exclusive || false} onChange={(e) => setEditingItem({ ...editingItem, is_exclusive: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Exclusive</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateItem('packages', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium">
              <Save className="w-4 h-4" /> Update
            </button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {packages.map((pkg) => (
          <div key={pkg.id} className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{pkg.name}</span>
                {pkg.is_featured && <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded-full">Featured</span>}
                {pkg.is_exclusive && <span className="px-2 py-0.5 text-xs bg-secondary/20 text-secondary rounded-full">Exclusive</span>}
              </div>
              <p className="text-sm text-muted-foreground">{pkg.price_display || `$${pkg.price?.toLocaleString()}`}</p>
              <p className="text-sm text-muted-foreground">{pkg.benefits?.length || 0} benefits</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs rounded-full ${pkg.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{pkg.is_active ? 'Active' : 'Hidden'}</span>
              <button onClick={() => { setEditingItem(pkg); setShowAddForm(false); }} className="p-2 hover:bg-muted rounded-lg"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => updateItem('packages', pkg.id, { is_active: !pkg.is_active })} className="p-2 hover:bg-muted rounded-lg">{pkg.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              <button onClick={() => deleteItem('packages', pkg.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {packages.length === 0 && <p className="text-center text-muted-foreground py-8">No packages yet.</p>}
      </div>
    </div>
  );

  const renderTicketPasses = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-outfit text-xl font-semibold">Registration Ticket Passes</h3>
        <button onClick={() => { setShowAddForm(true); setEditingItem(null); setNewItem({ is_active: true, order: ticketPasses.length, features: [], is_featured: false, is_sold_out: false }); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium">
          <Plus className="w-4 h-4" /> Add Pass
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && activeTab === 'ticket-passes' && (
        <div className="glass-card rounded-xl p-6 mb-6">
          <h4 className="font-semibold mb-4">New Ticket Pass</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Pass Name (e.g., Standard Pass) *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Price" value={newItem.price || ''} onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Price Display (e.g., $499)" value={newItem.price_display || ''} onChange={(e) => setNewItem({ ...newItem, price_display: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Badge Color (e.g., #0EA5E9)" value={newItem.badge_color || ''} onChange={(e) => setNewItem({ ...newItem, badge_color: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <textarea placeholder="Description" value={newItem.description || ''} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={2} />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Features/Inclusions (one per line)</label>
              <textarea placeholder="Full conference access&#10;Networking sessions&#10;Conference materials&#10;Lunch included" value={newItem.features?.join('\n') || ''} onChange={(e) => setNewItem({ ...newItem, features: e.target.value.split('\n').filter(Boolean) })} className="w-full px-4 py-3 rounded-xl bg-background border border-border" rows={4} />
            </div>
            <input type="number" placeholder="Max Quantity (leave empty for unlimited)" value={newItem.max_quantity || ''} onChange={(e) => setNewItem({ ...newItem, max_quantity: parseInt(e.target.value) || null })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Order" value={newItem.order || 0} onChange={(e) => setNewItem({ ...newItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newItem.is_featured || false} onChange={(e) => setNewItem({ ...newItem, is_featured: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newItem.is_sold_out || false} onChange={(e) => setNewItem({ ...newItem, is_sold_out: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Sold Out</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => createItem('ticket-passes', newItem)} disabled={!newItem.name} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingItem && activeTab === 'ticket-passes' && (
        <div className="glass-card rounded-xl p-6 mb-6 border-2 border-primary">
          <h4 className="font-semibold mb-4">Edit Ticket Pass</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" placeholder="Pass Name (e.g., Standard Pass) *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Price" value={editingItem.price || ''} onChange={(e) => setEditingItem({ ...editingItem, price: parseFloat(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Price Display (e.g., $499)" value={editingItem.price_display || ''} onChange={(e) => setEditingItem({ ...editingItem, price_display: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="text" placeholder="Badge Color (e.g., #0EA5E9)" value={editingItem.badge_color || ''} onChange={(e) => setEditingItem({ ...editingItem, badge_color: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <textarea placeholder="Description" value={editingItem.description || ''} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} className="md:col-span-2 px-4 py-3 rounded-xl bg-background border border-border" rows={2} />
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Features/Inclusions (one per line)</label>
              <textarea placeholder="Full conference access&#10;Networking sessions&#10;Conference materials&#10;Lunch included" value={editingItem.features?.join('\n') || ''} onChange={(e) => setEditingItem({ ...editingItem, features: e.target.value.split('\n').filter(Boolean) })} className="w-full px-4 py-3 rounded-xl bg-background border border-border" rows={4} />
            </div>
            <input type="number" placeholder="Max Quantity (leave empty for unlimited)" value={editingItem.max_quantity || ''} onChange={(e) => setEditingItem({ ...editingItem, max_quantity: parseInt(e.target.value) || null })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <input type="number" placeholder="Order" value={editingItem.order || 0} onChange={(e) => setEditingItem({ ...editingItem, order: parseInt(e.target.value) || 0 })} className="px-4 py-3 rounded-xl bg-background border border-border" />
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingItem.is_featured || false} onChange={(e) => setEditingItem({ ...editingItem, is_featured: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingItem.is_sold_out || false} onChange={(e) => setEditingItem({ ...editingItem, is_sold_out: e.target.checked })} className="w-4 h-4 rounded" />
                <span className="text-sm">Sold Out</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => updateItem('ticket-passes', editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium">
              <Save className="w-4 h-4" /> Update
            </button>
            <button onClick={() => setEditingItem(null)} className="flex items-center gap-2 px-6 py-3 glass-card rounded-xl font-medium">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {ticketPasses.map((pass) => (
          <div key={pass.id} className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className="w-3 h-12 rounded-full" style={{ backgroundColor: pass.badge_color || '#0EA5E9' }}></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{pass.name}</span>
                {pass.is_featured && <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded-full">Featured</span>}
                {pass.is_sold_out && <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">Sold Out</span>}
              </div>
              <p className="text-sm text-muted-foreground">{pass.price_display || `$${pass.price?.toLocaleString()}`}</p>
              <p className="text-sm text-muted-foreground">{pass.features?.length || 0} features included</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 text-xs rounded-full ${pass.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{pass.is_active ? 'Active' : 'Hidden'}</span>
              <button onClick={() => { setEditingItem(pass); setShowAddForm(false); }} className="p-2 hover:bg-muted rounded-lg"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => updateItem('ticket-passes', pass.id, { is_active: !pass.is_active })} className="p-2 hover:bg-muted rounded-lg">{pass.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              <button onClick={() => deleteItem('ticket-passes', pass.id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {ticketPasses.length === 0 && <p className="text-center text-muted-foreground py-8">No ticket passes yet. Add passes to show on the registration page.</p>}
      </div>
    </div>
  );

  // Helper to get content type label
  const getContentTypeLabel = (type) => {
    const labels = {
      'partners': 'Partner',
      'sponsor-heroes': 'Sponsor Banner',
      'agenda': 'Agenda Item',
      'speakers': 'Speaker',
      'packages': 'Sponsorship Package',
      'ticket-passes': 'Ticket Pass',
      'settings': 'Site Settings'
    };
    return labels[type] || type;
  };

  // Helper to get action badge color
  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'create': return 'bg-green-500/20 text-green-400';
      case 'update': return 'bg-amber-500/20 text-amber-400';
      case 'delete': return 'bg-red-500/20 text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  // Helper to render data diff
  const renderDataDiff = (oldData, newData, action) => {
    if (action === 'delete') {
      return (
        <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
          <p className="text-sm text-red-400 font-medium mb-2">Item to be deleted:</p>
          <div className="text-sm text-muted-foreground">
            <strong>{oldData?.name || oldData?.title || 'Item'}</strong>
          </div>
        </div>
      );
    }
    
    if (action === 'create') {
      return (
        <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
          <p className="text-sm text-green-400 font-medium mb-2">New item details:</p>
          <div className="space-y-1 text-sm">
            {newData?.name && <p><span className="text-muted-foreground">Name:</span> {newData.name}</p>}
            {newData?.title && <p><span className="text-muted-foreground">Title:</span> {newData.title}</p>}
            {newData?.description && <p><span className="text-muted-foreground">Description:</span> {newData.description?.substring(0, 100)}...</p>}
            {newData?.time && <p><span className="text-muted-foreground">Time:</span> {newData.time}</p>}
            {newData?.price !== undefined && <p><span className="text-muted-foreground">Price:</span> ${newData.price}</p>}
          </div>
        </div>
      );
    }
    
    // Update - show changes
    const changes = [];
    if (newData && oldData) {
      Object.keys(newData).forEach(key => {
        if (key !== 'updated_at' && key !== 'created_at' && key !== '_id' && JSON.stringify(newData[key]) !== JSON.stringify(oldData[key])) {
          changes.push({ key, old: oldData[key], new: newData[key] });
        }
      });
    }
    
    return (
      <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
        <p className="text-sm text-amber-400 font-medium mb-2">Changes:</p>
        <div className="space-y-2 text-sm">
          {changes.slice(0, 5).map((change, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span className="text-muted-foreground capitalize">{change.key.replace(/_/g, ' ')}:</span>
              <div className="flex-1">
                <span className="text-red-400 line-through">{typeof change.old === 'object' ? JSON.stringify(change.old) : String(change.old || '(empty)')}</span>
                <ArrowRight className="w-3 h-3 inline mx-1 text-muted-foreground" />
                <span className="text-green-400">{typeof change.new === 'object' ? JSON.stringify(change.new) : String(change.new || '(empty)')}</span>
              </div>
            </div>
          ))}
          {changes.length > 5 && <p className="text-muted-foreground">...and {changes.length - 5} more changes</p>}
          {changes.length === 0 && <p className="text-muted-foreground">No significant changes detected</p>}
        </div>
      </div>
    );
  };

  // Approvals Tab (for approver admin)
  const renderApprovals = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-outfit text-xl font-semibold">Pending Content Approvals</h3>
        <button onClick={loadAllData} className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl font-medium hover:bg-white/10">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <FileClock className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <p className="text-2xl font-bold">{pendingChanges.length}</p>
            <p className="text-sm text-muted-foreground">Changes awaiting approval</p>
          </div>
        </div>
      </div>

      {/* Pending Changes List */}
      <div className="space-y-4">
        {pendingChanges.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <FileCheck className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h4 className="font-semibold text-lg mb-2">All caught up!</h4>
            <p className="text-muted-foreground">No pending changes to review.</p>
          </div>
        ) : (
          pendingChanges.map((change) => (
            <div key={change.id} className="glass-card rounded-xl p-6" data-testid={`pending-change-${change.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getActionBadgeColor(change.action)}`}>
                      {change.action}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary/20 text-primary">
                      {getContentTypeLabel(change.content_type)}
                    </span>
                  </div>
                  <h4 className="font-semibold">
                    {change.new_data?.name || change.new_data?.title || change.old_data?.name || change.old_data?.title || 'Content Change'}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Submitted by <span className="text-primary">{change.submitted_by}</span> on {new Date(change.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              {/* Data Diff */}
              {renderDataDiff(change.old_data, change.new_data, change.action)}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleApproveChange(change.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-all"
                  data-testid={`approve-btn-${change.id}`}
                >
                  <CheckCircle className="w-4 h-4" /> Approve
                </button>
                <button
                  onClick={() => setShowRejectModal(change.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl font-medium transition-all"
                  data-testid={`reject-btn-${change.id}`}
                >
                  <XCircle className="w-4 h-4" /> Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-card rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-outfit text-xl font-bold mb-4">Reject Change</h3>
            <p className="text-muted-foreground mb-4">Please provide a reason for rejecting this change. This will be visible to the content editor.</p>
            <textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Reason for rejection (required)..."
              className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none resize-none h-32"
              data-testid="reject-notes-input"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => handleRejectChange(showRejectModal)}
                disabled={!rejectNotes.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-reject-btn"
              >
                <XCircle className="w-4 h-4" /> Confirm Rejection
              </button>
              <button
                onClick={() => { setShowRejectModal(null); setRejectNotes(''); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 glass-card rounded-xl font-medium hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // My Submissions Tab (for cmsadmin to see their pending/rejected items)
  const renderMySubmissions = () => {
    const pendingSubmissions = mySubmissions.filter(s => s.status === 'pending');
    const rejectedSubmissions = mySubmissions.filter(s => s.status === 'rejected');
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-outfit text-xl font-semibold">My Content Submissions</h3>
          <button onClick={loadAllData} className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl font-medium hover:bg-white/10">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingSubmissions.length}</p>
                <p className="text-sm text-muted-foreground">Pending Approval</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{rejectedSubmissions.length}</p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Submissions */}
        {pendingSubmissions.length > 0 && (
          <div>
            <h4 className="text-sm uppercase tracking-widest text-amber-400 mb-3">Awaiting Approval</h4>
            <div className="space-y-3">
              {pendingSubmissions.map((submission) => (
                <div key={submission.id} className="glass-card rounded-xl p-4 border-l-4 border-amber-500" data-testid={`submission-${submission.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getActionBadgeColor(submission.action)}`}>
                          {submission.action}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary">
                          {getContentTypeLabel(submission.content_type)}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400">
                          Pending
                        </span>
                      </div>
                      <h5 className="font-medium">
                        {submission.new_data?.name || submission.new_data?.title || submission.old_data?.name || submission.old_data?.title || 'Content Change'}
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        Submitted {new Date(submission.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rejected Submissions */}
        {rejectedSubmissions.length > 0 && (
          <div>
            <h4 className="text-sm uppercase tracking-widest text-red-400 mb-3">Rejected</h4>
            <div className="space-y-3">
              {rejectedSubmissions.map((submission) => (
                <div key={submission.id} className="glass-card rounded-xl p-4 border-l-4 border-red-500" data-testid={`submission-rejected-${submission.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getActionBadgeColor(submission.action)}`}>
                          {submission.action}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary/20 text-primary">
                          {getContentTypeLabel(submission.content_type)}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400">
                          Rejected
                        </span>
                      </div>
                      <h5 className="font-medium">
                        {submission.new_data?.name || submission.new_data?.title || submission.old_data?.name || submission.old_data?.title || 'Content Change'}
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        Submitted {new Date(submission.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · 
                        Rejected {submission.reviewed_at ? new Date(submission.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </p>
                    </div>
                    <XCircle className="w-5 h-5 text-red-400" />
                  </div>
                  {submission.review_notes && (
                    <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                      <p className="text-sm text-red-400 font-medium mb-1">Rejection Reason:</p>
                      <p className="text-sm text-muted-foreground">{submission.review_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {mySubmissions.length === 0 && (
          <div className="glass-card rounded-xl p-12 text-center">
            <FileCheck className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h4 className="font-semibold text-lg mb-2">No pending submissions</h4>
            <p className="text-muted-foreground">Your content changes will appear here while awaiting approval.</p>
          </div>
        )}
      </div>
    );
  };

  // Task CRUD operations
  const createTask = async (taskData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData)
      });
      if (!response.ok) throw new Error('Failed to create task');
      loadAllData();
      showSuccess('Task created successfully');
      setShowAddForm(false);
      setNewItem({});
    } catch (err) {
      setError(err.message);
    }
  };

  const updateTask = async (taskId, taskData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData)
      });
      if (!response.ok) throw new Error('Failed to update task');
      loadAllData();
      showSuccess('Task updated successfully');
      setEditingItem(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to delete task');
      loadAllData();
      showSuccess('Task deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const moveTask = async (taskId, direction) => {
    const taskIndex = tasks.findIndex(t => t.id === taskId);
    if (taskIndex === -1) return;
    const task = tasks[taskIndex];
    const samePhaseTaskes = tasks.filter(t => t.phase === task.phase).sort((a, b) => a.order - b.order);
    const currentIndex = samePhaseTaskes.findIndex(t => t.id === taskId);
    if (direction === 'up' && currentIndex > 0) {
      const prevTask = samePhaseTaskes[currentIndex - 1];
      await updateTask(taskId, { order: prevTask.order });
      await updateTask(prevTask.id, { order: task.order });
    } else if (direction === 'down' && currentIndex < samePhaseTaskes.length - 1) {
      const nextTask = samePhaseTaskes[currentIndex + 1];
      await updateTask(taskId, { order: nextTask.order });
      await updateTask(nextTask.id, { order: task.order });
    }
  };

  // Document CRUD operations
  const createDocument = async (docData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(docData)
      });
      if (!response.ok) throw new Error('Failed to create document');
      loadAllData();
      showSuccess('Document added successfully');
      setShowAddForm(false);
      setNewItem({});
    } catch (err) {
      setError(err.message);
    }
  };

  const updateDocument = async (docId, docData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents/${docId}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(docData)
      });
      if (!response.ok) throw new Error('Failed to update document');
      loadAllData();
      showSuccess('Document updated successfully');
      setEditingItem(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to delete document');
      loadAllData();
      showSuccess('Document deleted successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Tasks Tab
  const renderTasks = () => {
    const phases = ['Phase 1', 'Phase 2', 'Phase 3', 'Phase 4', 'Phase 5'];
    const categories = ['Contracts & Finance', 'Venues', 'Marketing & Assets', 'Sponsorship Sales', 'Programming', 'Attendee Management', 'Event Production', 'Design & Print', 'Final Details', 'Event Days'];
    const priorities = [
      { id: 'high', label: 'High', color: 'bg-red-500/20 text-red-400' },
      { id: 'medium', label: 'Medium', color: 'bg-amber-500/20 text-amber-400' },
      { id: 'low', label: 'Low', color: 'bg-green-500/20 text-green-400' }
    ];
    const statuses = [
      { id: 'not_started', label: 'Not Started', color: 'bg-slate-500/20 text-slate-400' },
      { id: 'in_progress', label: 'In Progress', color: 'bg-blue-500/20 text-blue-400' },
      { id: 'complete', label: 'Complete', color: 'bg-green-500/20 text-green-400' }
    ];
    
    // Helper to check deadline status
    const getDeadlineStatus = (dueDate, status) => {
      if (!dueDate || status === 'complete') return 'normal';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(dueDate);
      const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'overdue';
      if (diffDays <= 7) return 'upcoming';
      return 'normal';
    };
    
    // Get upcoming/overdue tasks for alerts section
    const urgentTasks = tasks.filter(task => {
      const deadlineStatus = getDeadlineStatus(task.due_date, task.status);
      return deadlineStatus === 'overdue' || deadlineStatus === 'upcoming';
    }).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    
    const filteredTasks = tasks.filter(task => {
      if (taskFilter !== 'all' && task.status !== taskFilter) return false;
      if (taskPhaseFilter !== 'all' && task.phase !== taskPhaseFilter) return false;
      return true;
    });
    const groupedTasks = phases.reduce((acc, phase) => {
      acc[phase] = filteredTasks.filter(t => t.phase === phase).sort((a, b) => {
        // First, sort by completion status (incomplete first, complete last)
        if (a.status === 'complete' && b.status !== 'complete') return 1;
        if (a.status !== 'complete' && b.status === 'complete') return -1;
        // Then sort by due date (earliest first, no date at end)
        const dateA = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
        const dateB = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
        return dateA - dateB;
      });
      return acc;
    }, {});
    const stats = { total: tasks.length, complete: tasks.filter(t => t.status === 'complete').length, inProgress: tasks.filter(t => t.status === 'in_progress').length, notStarted: tasks.filter(t => t.status === 'not_started').length };

    return (
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <h3 className="font-outfit text-xl font-semibold">Master To-Do List</h3>
          <button onClick={() => { setShowAddForm(true); setEditingItem(null); setNewItem({ phase: 'Phase 1', status: 'not_started', priority: 'medium', order: tasks.length }); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium" data-testid="add-task-btn">
            <Plus className="w-4 h-4" /> Add Task
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card rounded-xl p-4"><p className="text-2xl font-bold">{stats.total}</p><p className="text-sm text-muted-foreground">Total Tasks</p></div>
          <div className="glass-card rounded-xl p-4 border-l-4 border-green-500"><p className="text-2xl font-bold text-green-400">{stats.complete}</p><p className="text-sm text-muted-foreground">Complete</p></div>
          <div className="glass-card rounded-xl p-4 border-l-4 border-blue-500"><p className="text-2xl font-bold text-blue-400">{stats.inProgress}</p><p className="text-sm text-muted-foreground">In Progress</p></div>
          <div className="glass-card rounded-xl p-4 border-l-4 border-slate-500"><p className="text-2xl font-bold text-slate-400">{stats.notStarted}</p><p className="text-sm text-muted-foreground">Not Started</p></div>
        </div>
        
        {/* Urgent Deadlines Alert Section */}
        {urgentTasks.length > 0 && (
          <div className="glass-card rounded-xl p-4 border-l-4 border-red-500 bg-red-500/5">
            <h4 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Upcoming & Overdue Deadlines ({urgentTasks.length})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-red-500/20">
                    <th className="text-left p-2 font-medium text-red-300">Task</th>
                    <th className="text-left p-2 font-medium text-red-300">Due Date</th>
                    <th className="text-left p-2 font-medium text-red-300">Owner</th>
                    <th className="text-left p-2 font-medium text-red-300">Status</th>
                    <th className="text-left p-2 font-medium text-red-300">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {urgentTasks.map(task => {
                    const deadlineStatus = getDeadlineStatus(task.due_date, task.status);
                    return (
                      <tr key={task.id} className={`border-b border-red-500/10 ${deadlineStatus === 'overdue' ? 'bg-red-500/10' : ''}`}>
                        <td className={`p-2 font-medium ${deadlineStatus === 'overdue' ? 'text-red-400' : ''}`}>{task.title}</td>
                        <td className={`p-2 ${deadlineStatus === 'overdue' ? 'text-red-400 font-bold' : 'text-amber-400'}`}>
                          {task.due_date} {deadlineStatus === 'overdue' ? '(OVERDUE)' : '(This Week)'}
                        </td>
                        <td className="p-2 text-muted-foreground">{task.owner || '—'}</td>
                        <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${statuses.find(s => s.id === task.status)?.color || ''}`}>{statuses.find(s => s.id === task.status)?.label}</span></td>
                        <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${priorities.find(p => p.id === task.priority)?.color || ''}`}>{task.priority}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        <div className="flex flex-wrap gap-2">
          <select value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)} className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
            <option value="all">All Statuses</option>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select value={taskPhaseFilter} onChange={(e) => setTaskPhaseFilter(e.target.value)} className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
            <option value="all">All Phases</option>
            {phases.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        {showAddForm && !editingItem && (
          <div className="glass-card rounded-xl p-6">
            <h4 className="font-semibold mb-4">Add New Task</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Task Title *" value={newItem.title || ''} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <select value={newItem.phase || 'Phase 1'} onChange={(e) => setNewItem({ ...newItem, phase: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{phases.map(p => <option key={p} value={p}>{p}</option>)}</select>
              <select value={newItem.category || ''} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border"><option value="">Select Category</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
              <select value={newItem.priority || 'medium'} onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{priorities.map(p => <option key={p.id} value={p.id}>{p.label} Priority</option>)}</select>
              <input type="text" placeholder="Owner" value={newItem.owner || ''} onChange={(e) => setNewItem({ ...newItem, owner: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <input type="date" placeholder="Due Date" value={newItem.due_date || ''} onChange={(e) => setNewItem({ ...newItem, due_date: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <textarea placeholder="Description" value={newItem.description || ''} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border md:col-span-2" rows={2} />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => createTask(newItem)} disabled={!newItem.title} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
              <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="px-6 py-3 glass-card rounded-xl font-medium">Cancel</button>
            </div>
          </div>
        )}
        {editingItem && activeTab === 'tasks' && (
          <div className="glass-card rounded-xl p-6">
            <h4 className="font-semibold mb-4">Edit Task</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Task Title *" value={editingItem.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <select value={editingItem.phase || 'Phase 1'} onChange={(e) => setEditingItem({ ...editingItem, phase: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{phases.map(p => <option key={p} value={p}>{p}</option>)}</select>
              <select value={editingItem.category || ''} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border"><option value="">Select Category</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
              <select value={editingItem.status || 'not_started'} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</select>
              <select value={editingItem.priority || 'medium'} onChange={(e) => setEditingItem({ ...editingItem, priority: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{priorities.map(p => <option key={p.id} value={p.id}>{p.label} Priority</option>)}</select>
              <input type="text" placeholder="Owner" value={editingItem.owner || ''} onChange={(e) => setEditingItem({ ...editingItem, owner: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <input type="date" placeholder="Due Date" value={editingItem.due_date || ''} onChange={(e) => setEditingItem({ ...editingItem, due_date: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <textarea placeholder="Description" value={editingItem.description || ''} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border md:col-span-2" rows={2} />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => updateTask(editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"><Save className="w-4 h-4" /> Update</button>
              <button onClick={() => setEditingItem(null)} className="px-6 py-3 glass-card rounded-xl font-medium">Cancel</button>
            </div>
          </div>
        )}
        
        {/* Tasks Table View */}
        <div className="space-y-6">
          {phases.map(phase => {
            const phaseTasks = groupedTasks[phase] || [];
            if (phaseTasks.length === 0 && taskPhaseFilter !== 'all' && taskPhaseFilter !== phase) return null;
            return (
              <div key={phase} className="glass-card rounded-xl p-4 overflow-hidden">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">{phase.split(' ')[1]}</span>
                  {phase}
                  <span className="text-sm font-normal text-muted-foreground">({phaseTasks.length} tasks)</span>
                </h4>
                {phaseTasks.length === 0 ? <p className="text-muted-foreground text-sm py-4">No tasks in this phase</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left p-3 font-medium text-muted-foreground w-8"></th>
                          <th className="text-left p-3 font-medium text-muted-foreground w-8"></th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Task</th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Category</th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Owner</th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Deadline</th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                          <th className="text-left p-3 font-medium text-muted-foreground">Priority</th>
                          <th className="text-left p-3 font-medium text-muted-foreground w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {phaseTasks.map((task, index) => {
                          const deadlineStatus = getDeadlineStatus(task.due_date, task.status);
                          return (
                            <tr key={task.id} className={`border-b border-border/50 hover:bg-white/5 ${task.status === 'complete' ? 'bg-green-500/5' : deadlineStatus === 'overdue' ? 'bg-red-500/5' : ''}`} data-testid={`task-${task.id}`}>
                              <td className="p-2">
                                <div className="flex flex-col gap-0.5">
                                  <button onClick={() => moveTask(task.id, 'up')} disabled={index === 0} className="p-1 hover:bg-white/10 rounded disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                                  <button onClick={() => moveTask(task.id, 'down')} disabled={index === phaseTasks.length - 1} className="p-1 hover:bg-white/10 rounded disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                                </div>
                              </td>
                              <td className="p-2">
                                <input type="checkbox" checked={task.status === 'complete'} onChange={() => updateTask(task.id, { status: task.status === 'complete' ? 'not_started' : 'complete' })} className="w-4 h-4 rounded border-2 cursor-pointer accent-green-500" />
                              </td>
                              <td className={`p-3 font-medium ${task.status === 'complete' ? 'line-through text-muted-foreground' : ''}`}>{task.title}</td>
                              <td className="p-3">{task.category ? <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">{task.category}</span> : <span className="text-muted-foreground">—</span>}</td>
                              <td className="p-3 text-muted-foreground">{task.owner || '—'}</td>
                              <td className={`p-3 font-medium ${deadlineStatus === 'overdue' ? 'text-red-400' : deadlineStatus === 'upcoming' ? 'text-amber-400' : 'text-muted-foreground'}`}>
                                {task.due_date || '—'}
                                {deadlineStatus === 'overdue' && <span className="ml-1 text-xs">(OVERDUE)</span>}
                              </td>
                              <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${statuses.find(s => s.id === task.status)?.color || ''}`}>{statuses.find(s => s.id === task.status)?.label}</span></td>
                              <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${priorities.find(p => p.id === task.priority)?.color || ''}`}>{task.priority}</span></td>
                              <td className="p-3">
                                <div className="flex gap-1">
                                  <button onClick={() => { setEditingItem(task); setShowAddForm(false); }} className="p-1.5 text-primary hover:bg-primary/10 rounded"><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => deleteTask(task.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {tasks.length === 0 && <p className="text-center text-muted-foreground py-8">No tasks yet. Click "Add Task" to create your first task.</p>}
      </div>
    );
  };

  // Documents Tab
  const renderDocuments = () => {
    const categories = ['General', 'Contracts', 'Marketing', 'Venues', 'Sponsors', 'Speakers', 'Operations', 'Finance'];
    const providers = [{ id: 'google_drive', label: 'Google Drive', icon: '📁' }, { id: 'onedrive', label: 'OneDrive', icon: '☁️' }, { id: 'sharepoint', label: 'SharePoint', icon: '📂' }, { id: 'dropbox', label: 'Dropbox', icon: '📦' }, { id: 'other', label: 'Other', icon: '🔗' }];
    const filteredDocs = documentFilter === 'all' ? documents : documents.filter(d => d.category === documentFilter);
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <h3 className="font-outfit text-xl font-semibold">Shared Documents</h3>
          <button onClick={() => { setShowAddForm(true); setEditingItem(null); setNewItem({ category: 'General', provider: 'other' }); }} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium" data-testid="add-document-btn"><Plus className="w-4 h-4" /> Add Document</button>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={documentFilter} onChange={(e) => setDocumentFilter(e.target.value)} className="px-3 py-2 rounded-lg bg-background border border-border text-sm"><option value="all">All Categories</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
        </div>
        {showAddForm && !editingItem && activeTab === 'documents' && (
          <div className="glass-card rounded-xl p-6">
            <h4 className="font-semibold mb-4">Add Document Link</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Document Name *" value={newItem.name || ''} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <select value={newItem.category || 'General'} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
              <input type="url" placeholder="Share Link (URL) *" value={newItem.url || ''} onChange={(e) => setNewItem({ ...newItem, url: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border md:col-span-2" />
              <select value={newItem.provider || 'other'} onChange={(e) => setNewItem({ ...newItem, provider: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{providers.map(p => <option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}</select>
              <textarea placeholder="Description (optional)" value={newItem.description || ''} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" rows={2} />
            </div>
            <div className="flex gap-2 mt-4">
              <button 
                onClick={() => {
                  console.log('Creating document with data:', newItem);
                  createDocument(newItem);
                }} 
                disabled={!newItem.name || !newItem.url} 
                className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" /> Save
              </button>
              <button onClick={() => { setShowAddForm(false); setNewItem({}); }} className="px-6 py-3 glass-card rounded-xl font-medium">Cancel</button>
            </div>
            {(!newItem.name || !newItem.url) && (
              <p className="text-sm text-amber-500 mt-2">Please fill in both Document Name and Share Link to enable Save</p>
            )}
          </div>
        )}
        {editingItem && activeTab === 'documents' && (
          <div className="glass-card rounded-xl p-6">
            <h4 className="font-semibold mb-4">Edit Document</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Document Name *" value={editingItem.name || ''} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" />
              <select value={editingItem.category || 'General'} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
              <input type="url" placeholder="Share Link (URL) *" value={editingItem.url || ''} onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border md:col-span-2" />
              <select value={editingItem.provider || 'other'} onChange={(e) => setEditingItem({ ...editingItem, provider: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border">{providers.map(p => <option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}</select>
              <textarea placeholder="Description (optional)" value={editingItem.description || ''} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} className="px-4 py-3 rounded-xl bg-background border border-border" rows={2} />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => updateDocument(editingItem.id, editingItem)} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium"><Save className="w-4 h-4" /> Update</button>
              <button onClick={() => setEditingItem(null)} className="px-6 py-3 glass-card rounded-xl font-medium">Cancel</button>
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocs.map(doc => {
            const provider = providers.find(p => p.id === doc.provider) || providers[4];
            return (
              <div key={doc.id} className="glass-card rounded-xl p-4 hover:border-primary/50 transition-all" data-testid={`document-${doc.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{provider.icon}</span>
                    <div><h4 className="font-medium">{doc.name}</h4><span className="text-xs text-muted-foreground">{doc.category}</span></div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingItem(doc); setShowAddForm(false); }} className="p-1.5 text-primary hover:bg-primary/10 rounded"><Edit2 className="w-3 h-3" /></button>
                    <button onClick={() => deleteDocument(doc.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
                {doc.description && <p className="text-sm text-muted-foreground mb-3">{doc.description}</p>}
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline"><Link2 className="w-4 h-4" /> Open Document <ExternalLink className="w-3 h-3" /></a>
                <p className="text-xs text-muted-foreground mt-2">Added by {doc.uploaded_by} • {new Date(doc.created_at).toLocaleDateString()}</p>
              </div>
            );
          })}
        </div>
        {documents.length === 0 && <p className="text-center text-muted-foreground py-8">No documents yet. Click "Add Document" to share your first document link.</p>}
      </div>
    );
  };

  // Main Dashboard
  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-24 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-outfit text-3xl font-bold mb-2">
                {hasFullAccess ? 'Admin Portal' : 'Operations Portal'}
              </h1>
              <p className="text-muted-foreground">
                {hasFullAccess ? 'Manage registrations, sponsors, content, and themes' : 'Manage registrations, sponsors, and invites'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground hidden md:flex flex-col items-end">
                <span>Logged in as <span className="text-primary font-medium">{credentials.username}</span></span>
                <span className={`text-xs ${hasFullAccess ? 'text-green-400' : 'text-secondary'}`}>
                  {hasFullAccess ? 'Full Access' : 'Operations Only'}
                </span>
              </div>
              <button onClick={loadAllData} disabled={isLoading} className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl font-medium transition-all hover:bg-white/10">
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl font-medium transition-all hover:bg-destructive/20 hover:text-destructive" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Success/Error Messages */}
      {(successMessage || error) && (
        <section className="px-6 md:px-12 lg:px-24 mb-6">
          <div className="max-w-7xl mx-auto">
            {successMessage && (
              <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400">{successMessage}</span>
              </div>
            )}
            {error && (
              <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/30 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <span className="text-destructive">{error}</span>
                <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="px-6 md:px-12 lg:px-24 mb-6">
        <div className="max-w-7xl mx-auto">
          {/* Category Headers */}
          <div className="mb-4">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Operations</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {tabs.filter(t => t.category === 'ops').map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setShowAddForm(false); setNewItem({}); setEditingItem(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'glass-card hover:bg-white/10'}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full">{tab.count}</span>}
              </button>
            ))}
          </div>
          
          {/* Workspace Section - Tasks & Documents */}
          <div className="mb-4">
            <span className="text-xs uppercase tracking-widest text-emerald-400">Workspace</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {tabs.filter(t => t.category === 'workspace').map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setShowAddForm(false); setNewItem({}); setEditingItem(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === tab.id ? 'bg-emerald-500 text-white' : tab.highlight ? 'bg-emerald-500/20 text-emerald-400' : 'glass-card hover:bg-white/10'}`}
                data-testid={`${tab.id}-tab-btn`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full">{tab.count}</span>}
              </button>
            ))}
          </div>
          
          <div className="mb-4">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Content Management</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {tabs.filter(t => t.category === 'content').map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setShowAddForm(false); setNewItem({}); setEditingItem(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'glass-card hover:bg-white/10'}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full">{tab.count}</span>}
              </button>
            ))}
          </div>
          
          {/* Approvals Section - Only for approver */}
          {isApprover && (
            <>
              <div className="mb-4">
                <span className="text-xs uppercase tracking-widest text-amber-400">Approvals</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {tabs.filter(t => t.category === 'approvals').map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setShowAddForm(false); setNewItem({}); setEditingItem(null); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === tab.id ? 'bg-amber-500 text-white' : tab.highlight ? 'bg-amber-500/20 text-amber-400 animate-pulse' : 'glass-card hover:bg-white/10'}`}
                    data-testid="approvals-tab-btn"
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {tab.count > 0 && <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full">{tab.count}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
          
          {/* My Submissions Section - Only for cmsadmin (non-approver) */}
          {!isApprover && hasFullAccess && (
            <>
              <div className="mb-4">
                <span className="text-xs uppercase tracking-widest text-cyan-400">My Submissions</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {tabs.filter(t => t.category === 'submissions').map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setShowAddForm(false); setNewItem({}); setEditingItem(null); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === tab.id ? 'bg-cyan-500 text-white' : tab.highlight ? 'bg-cyan-500/20 text-cyan-400 animate-pulse' : 'glass-card hover:bg-white/10'}`}
                    data-testid="my-submissions-tab-btn"
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {tab.count > 0 && <span className="px-2 py-0.5 text-xs bg-white/20 rounded-full">{tab.count}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
          
          <div className="mb-4">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Settings</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {tabs.filter(t => t.category === 'settings').map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setShowAddForm(false); setNewItem({}); setEditingItem(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'glass-card hover:bg-white/10'}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            {activeTab === 'registrations' && renderRegistrations()}
            {activeTab === 'sponsors' && renderSponsors()}
            {activeTab === 'invites' && renderInvites()}
            {activeTab === 'partners' && renderPartners()}
            {activeTab === 'hero-sponsors' && renderSponsorHeroes()}
            {activeTab === 'agenda' && renderAgenda()}
            {activeTab === 'speakers' && renderSpeakers()}
            {activeTab === 'packages' && renderPackages()}
            {activeTab === 'ticket-passes' && renderTicketPasses()}
            {activeTab === 'tasks' && renderTasks()}
            {activeTab === 'documents' && renderDocuments()}
            {activeTab === 'my-submissions' && renderMySubmissions()}
            {activeTab === 'approvals' && renderApprovals()}
            {activeTab === 'page-builder' && renderPageBuilder()}
            {activeTab === 'site-config' && renderSiteConfig()}
            {activeTab === 'global-styling' && <GlobalStylingTab credentials={credentials} onSuccess={showSuccess} />}
            {activeTab === 'theme' && renderThemeSettings()}
            {activeTab === 'tenants' && <TenantManagementTab credentials={credentials} onSuccess={showSuccess} />}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CMSAdminPage;
